import esriConfig from "@arcgis/core/config";
import * as projection from "@arcgis/core/geometry/projection";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as XLSX from 'xlsx/xlsx.mjs';
import api, { enrichmentCacheAPI } from './api';  // Add this at the top with other imports
import { flipVertical } from "@arcgis/core/geometry/geometryEngine";

// Import USA data from combined .md file
import usaDataRaw from '../services/usaData.md?raw';

// Custom export names storage key (matches VariableExportNamesEditor)
const EXPORT_NAMES_STORAGE_KEY = 'variableExportNames';

// Get custom export names from localStorage
const getCustomExportNames = () => {
  try {
    const stored = localStorage.getItem(EXPORT_NAMES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const ARCGIS_CLIENT_ID = import.meta.env.VITE_ARCGIS_CLIENT_ID;
const ARCGIS_CLIENT_SECRET = import.meta.env.VITE_ARCGIS_CLIENT_SECRET;

if (!ARCGIS_CLIENT_ID || !ARCGIS_CLIENT_SECRET) {
  console.error("ArcGIS credentials not found in environment variables");
}

// Set the API key for ArcGIS configuration
esriConfig.apiKey = ARCGIS_CLIENT_ID;

// Constants
const CHUNK_SIZE = 100; // Process 100 areas per batch (larger batches = fewer API calls)
const BATCH_DELAY_MS = 0; // No delay needed - batches run in parallel now
const MAX_RETRIES = 3; // Maximum number of retry attempts for failed requests
const INITIAL_RETRY_DELAY_MS = 1000; // Initial delay before first retry (will increase exponentially)

const MA_TYPE_MAPPING = {
  radius: "RADIUS",
  place: "PLACE",
  block: "BLOCK",
  blockgroup: "BLOCKGROUP",
  cbsa: "CBSA",
  state: "STATE",
  zip: "ZIP",
  tract: "TRACT",
  county: "COUNTY",
};

/**
 * Parse USA data from .md file content into a label-value map
 * @param {string} mdContent - Raw content from .md file
 * @returns {Map<string, string>} - Map of variable labels to values
 */
function parseUSADataToMap(mdContent) {
  const dataMap = new Map();
  const lines = mdContent.trim().split('\n');

  for (const line of lines) {
    // Skip empty lines and comment/header lines (starting with #)
    if (!line.trim() || line.trim().startsWith('#')) continue;

    const parts = line.split('\t');
    if (parts.length >= 2) {
      const label = parts[0].trim();
      const value = parts[1].trim();
      dataMap.set(label, value);
    }
  }

  return dataMap;
}

// Parse USA data from the combined file
const usaDataMap = parseUSADataToMap(usaDataRaw);


// Helper function to get dynamic labels based on vintage year
const getLabelsForYear = (vintageYear = '2025') => {
  const currentYear = vintageYear === '2024' ? '2024' : '2025';
  const futureYear = vintageYear === '2024' ? '2029' : '2030';
  return { currentYear, futureYear };
};

// Function to update variable labels based on vintage year
const updateVariableLabels = (variables, vintageYear = '2025') => {
  const { currentYear, futureYear } = getLabelsForYear(vintageYear);
  
  return variables.map(variable => {
    let updatedLabel = variable.label;
    
    // Replace year patterns in labels
    // Replace 2025 with current year and 2030 with future year
    updatedLabel = updatedLabel.replace(/2025/g, currentYear);
    updatedLabel = updatedLabel.replace(/2030/g, futureYear);
    
    // Also handle 2024/2029 patterns if they exist
    updatedLabel = updatedLabel.replace(/2024/g, currentYear);
    updatedLabel = updatedLabel.replace(/2029/g, futureYear);
    
    return {
      ...variable,
      label: updatedLabel
    };
  });
};

export const analysisCategories = {
  tier1: {
    label: "Core (Tier 1) Variables",
    variables: [
      // Current Year Population Base
      { id: "AtRisk.TOTPOP_CY", label: "2025 Total Population" },
      { id: "AtRisk.TOTHH_CY", label: "2025 Total Households" },
      { id: "AtRisk.AVGHHSZ_CY", label: "2025 Average Household Size" },

      // Current Year Population by Age
      { id: "5yearincrements.POP0_CY", label: "2025 Population Age 0-4" },
      { id: "5yearincrements.POP5_CY", label: "2025 Population Age 5-9" },
      { id: "5yearincrements.POP10_CY", label: "2025 Population Age 10-14" },
      { id: "5yearincrements.POP15_CY", label: "2025 Population Age 15-19" },
      { id: "5yearincrements.POP20_CY", label: "2025 Population Age 20-24" },
      { id: "5yearincrements.POP25_CY", label: "2025 Population Age 25-29" },
      { id: "5yearincrements.POP30_CY", label: "2025 Population Age 30-34" },
      { id: "5yearincrements.POP35_CY", label: "2025 Population Age 35-39" },
      { id: "5yearincrements.POP40_CY", label: "2025 Population Age 40-44" },
      { id: "5yearincrements.POP45_CY", label: "2025 Population Age 45-49" },
      { id: "5yearincrements.POP50_CY", label: "2025 Population Age 50-54" },
      { id: "5yearincrements.POP55_CY", label: "2025 Population Age 55-59" },
      { id: "5yearincrements.POP60_CY", label: "2025 Population Age 60-64" },
      { id: "5yearincrements.POP65_CY", label: "2025 Population Age 65-69" },
      { id: "5yearincrements.POP70_CY", label: "2025 Population Age 70-74" },
      { id: "5yearincrements.POP75_CY", label: "2025 Population Age 75-79" },
      { id: "5yearincrements.POP80_CY", label: "2025 Population Age 80-84" },
      { id: "5yearincrements.POP85_CY", label: "2025 Population Age 85+" },
      { id: "5yearincrements.MEDAGE_CY", label: "2025 Median Age" },

      // Current Year Daytime Population
      { id: "DaytimePopulation.DPOPWRK_CY", label: "2025 Daytime Pop: Workers" },

      // Current Year Household Income
      { id: "householdincome.HINC0_CY", label: "2025 HH Income <$15000" },
      { id: "householdincome.HINC15_CY", label: "2025 HH Income $15000-24999" },
      { id: "householdincome.HINC25_CY", label: "2025 HH Income $25000-34999" },
      { id: "householdincome.HINC35_CY", label: "2025 HH Income $35000-49999" },
      { id: "householdincome.HINC50_CY", label: "2025 HH Income $50000-74999" },
      { id: "householdincome.HINC75_CY", label: "2025 HH Income $75000-99999" },
      { id: "householdincome.HINC100_CY", label: "2025 HH Income $100000-149999" },
      { id: "householdincome.HINC150_CY", label: "2025 HH Income $150000-199999" },
      { id: "householdincome.HINC200_CY", label: "2025 HH Income $200000+" },
      { id: "householdincome.MEDHINC_CY", label: "2025 Median Household Income" },
      { id: "householdincome.AVGHINC_CY", label: "2025 Average Household Income" },

      // Current Year Income by Age Groups - Organized with base before details
      { id: "incomebyage.IA15BASECY", label: "2025 HH Income Base: HHr 15-24" },

      // Age 25-34 Income
      { id: "incomebyage.A25I0_CY", label: "2025 HH Inc <$15000/HHr 25-34" },
      { id: "incomebyage.A25I15_CY", label: "2025 HH Inc $15K-24999/HHr 25-34" },
      { id: "incomebyage.A25I25_CY", label: "2025 HH Inc $25K-34999/HHr 25-34" },
      { id: "incomebyage.A25I35_CY", label: "2025 HH Inc $35K-49999/HHr 25-34" },
      { id: "incomebyage.A25I50_CY", label: "2025 HH Inc $50K-74999/HHr 25-34" },
      { id: "incomebyage.A25I75_CY", label: "2025 HH Inc $75K-99999/HHr 25-34" },
      { id: "incomebyage.A25I100_CY", label: "2025 HH Inc 100K-149999/HHr 25-34" },
      { id: "incomebyage.A25I150_CY", label: "2025 HH Inc 150K-199999/HHr 25-34" },
      { id: "incomebyage.A25I200_CY", label: "2025 HH Inc $200000+/HHr 25-34" },
      { id: "incomebyage.IA25BASECY", label: "2025 HH Income Base: HHr 25-34" },

      // Age 35-44 Income
      { id: "incomebyage.A35I0_CY", label: "2025 HH Inc <$15000/HHr 35-44" },
      { id: "incomebyage.A35I15_CY", label: "2025 HH Inc $15K-24999/HHr 35-44" },
      { id: "incomebyage.A35I25_CY", label: "2025 HH Inc $25K-34999/HHr 35-44" },
      { id: "incomebyage.A35I35_CY", label: "2025 HH Inc $35K-49999/HHr 35-44" },
      { id: "incomebyage.A35I50_CY", label: "2025 HH Inc $50K-74999/HHr 35-44" },
      { id: "incomebyage.A35I75_CY", label: "2025 HH Inc $75K-99999/HHr 35-44" },
      { id: "incomebyage.A35I100_CY", label: "2025 HH Inc 100K-149999/HHr 35-44" },
      { id: "incomebyage.A35I150_CY", label: "2025 HH Inc 150K-199999/HHr 35-44" },
      { id: "incomebyage.A35I200_CY", label: "2025 HH Inc $200000+/HHr 35-44" },
      { id: "incomebyage.IA35BASECY", label: "2025 HH Income Base: HHr 35-44" },

      // Age 45-54 Income
      { id: "incomebyage.A45I0_CY", label: "2025 HH Inc <$15000/HHr 45-54" },
      { id: "incomebyage.A45I15_CY", label: "2025 HH Inc $15K-24999/HHr 45-54" },
      { id: "incomebyage.A45I25_CY", label: "2025 HH Inc $25K-34999/HHr 45-54" },
      { id: "incomebyage.A45I35_CY", label: "2025 HH Inc $35K-49999/HHr 45-54" },
      { id: "incomebyage.A45I50_CY", label: "2025 HH Inc $50K-74999/HHr 45-54" },
      { id: "incomebyage.A45I75_CY", label: "2025 HH Inc $75K-99999/HHr 45-54" },
      { id: "incomebyage.A45I100_CY", label: "2025 HH Inc 100K-149999/HHr 45-54" },
      { id: "incomebyage.A45I150_CY", label: "2025 HH Inc 150K-199999/HHr 45-54" },
      { id: "incomebyage.A45I200_CY", label: "2025 HH Inc $200000+/HHr 45-54" },
      { id: "incomebyage.IA45BASECY", label: "2025 HH Income Base: HHr 45-54" },
      // Other Age Group Income Bases
      { id: "incomebyage.IA55BASECY", label: "2025 HH Income Base: HHr 55-64" },
      { id: "incomebyage.IA65BASECY", label: "2025 HH Income Base: HHr 65-74" },
      { id: "incomebyage.IA75BASECY", label: "2025 HH Income Base: HHr 75+" },

      // Current Year Net Worth
      { id: "networth.NW0_CY", label: "2025 Net Worth <$15000" },
      { id: "networth.NW15_CY", label: "2025 Net Worth $15000-$34999" },
      { id: "networth.NW35_CY", label: "2025 Net Worth $35000-$49999" },
      { id: "networth.NW50_CY", label: "2025 Net Worth $50000-$74999" },
      { id: "networth.NW75_CY", label: "2025 Net Worth $75000-$99999" },
      { id: "networth.NW100_CY", label: "2025 Net Worth $100000-$149999" },
      { id: "networth.NW150_CY", label: "2025 Net Worth $150000-$249999" },
      { id: "networth.NW250_CY", label: "2025 Net Worth $250000-$499999" },
      { id: "networth.NW500_CY", label: "2025 Net Worth $500000-$999999" },
      { id: "networth.NW1M_CY", label: "2025 Net Worth $1000000-$1499999" },
      { id: "networth.NW1PT5M_CY", label: "2025 Net Worth $1500000-$1999999" },
      { id: "networth.NW2M_CY", label: "2025 Net Worth $2000000+" },
      { id: "networth.MEDNW_CY", label: "2025 Median Net Worth" },
      { id: "networth.AVGNW_CY", label: "2025 Average Net Worth" },

      // Current Year Housing
      { id: "KeyUSFacts.TOTHU_CY", label: "2025 Total Housing Units" },
      { id: "KeyUSFacts.OWNER_CY", label: "2025 Owner Occupied HUs" },
      { id: "KeyUSFacts.RENTER_CY", label: "2025 Renter Occupied HUs" },
      { id: "KeyUSFacts.VACANT_CY", label: "2025 Vacant Housing Units" },

      // Future Year Population
      { id: "KeyUSFacts.TOTPOP_FY", label: "2030 Total Population" },
      { id: "KeyUSFacts.TOTHH_FY", label: "2030 Total Households" },
      { id: "householdtotals.AVGHHSZ_FY", label: "2030 Average Household Size" },

      // Future Year Population by Age
      { id: "5yearincrements.POP0_FY", label: "2030 Population Age 0-4" },
      { id: "5yearincrements.POP5_FY", label: "2030 Population Age 5-9" },
      { id: "5yearincrements.POP10_FY", label: "2030 Population Age 10-14" },
      { id: "5yearincrements.POP15_FY", label: "2030 Population Age 15-19" },
      { id: "5yearincrements.POP20_FY", label: "2030 Population Age 20-24" },
      { id: "5yearincrements.POP25_FY", label: "2030 Population Age 25-29" },
      { id: "5yearincrements.POP30_FY", label: "2030 Population Age 30-34" },
      { id: "5yearincrements.POP35_FY", label: "2030 Population Age 35-39" },
      { id: "5yearincrements.POP40_FY", label: "2030 Population Age 40-44" },
      { id: "5yearincrements.POP45_FY", label: "2030 Population Age 45-49" },
      { id: "5yearincrements.POP50_FY", label: "2030 Population Age 50-54" },
      { id: "5yearincrements.POP55_FY", label: "2030 Population Age 55-59" },
      { id: "5yearincrements.POP60_FY", label: "2030 Population Age 60-64" },
      { id: "5yearincrements.POP65_FY", label: "2030 Population Age 65-69" },
      { id: "5yearincrements.POP70_FY", label: "2030 Population Age 70-74" },
      { id: "5yearincrements.POP75_FY", label: "2030 Population Age 75-79" },
      { id: "5yearincrements.POP80_FY", label: "2030 Population Age 80-84" },
      { id: "5yearincrements.POP85_FY", label: "2030 Population Age 85+" },
      { id: "5yearincrements.MEDAGE_FY", label: "2030 Median Age" },

      // Future Year Household Income
      { id: "householdincome.HINC0_FY", label: "2030 HH Income <$15000" },
      { id: "householdincome.HINC15_FY", label: "2030 HH Income $15000-24999" },
      { id: "householdincome.HINC25_FY", label: "2030 HH Income $25000-34999" },
      { id: "householdincome.HINC35_FY", label: "2030 HH Income $35000-49999" },
      { id: "householdincome.HINC50_FY", label: "2030 HH Income $50000-74999" },
      { id: "householdincome.HINC75_FY", label: "2030 HH Income $75000-99999" },
      { id: "householdincome.HINC100_FY", label: "2030 HH Income $100000-149999" },
      { id: "householdincome.HINC150_FY", label: "2030 HH Income $150000-199999" },
      { id: "householdincome.HINC200_FY", label: "2030 HH Income $200000+" },
      { id: "householdincome.MEDHINC_FY", label: "2030 Median Household Income" },
      { id: "householdincome.AVGHINC_FY", label: "2030 Average Household Income" },

      // Future Year Housing
      { id: "KeyUSFacts.TOTHU_FY", label: "2030 Total Housing Units" },
      { id: "KeyUSFacts.OWNER_FY", label: "2030 Owner Occupied HUs" },
      { id: "KeyUSFacts.RENTER_FY", label: "2030 Renter Occupied HUs" },
      { id: "KeyUSFacts.VACANT_FY", label: "2030 Vacant Housing Units" },

      { id: "HistoricalPopulation.TSPOP20_CY", label: "2020 Total Population" },
      { id: "HistoricalHouseholds.TSHH20_CY", label: "2020 Total Households" },

      // Historical (2010) Population
      { id: "HistoricalPopulation.TSPOP10_CY", label: "2010 Total Population" },
      { id: "HistoricalHouseholds.TSHH10_CY", label: "2010 Total Households" },
      { id: "5yearincrements.POP0C10", label: "2010 Population Age 0-4" },
      { id: "5yearincrements.POP5C10", label: "2010 Population Age 5-9" },
      { id: "5yearincrements.POP10C10", label: "2010 Population Age 10-14" },
      { id: "5yearincrements.POP15C10", label: "2010 Population Age 15-19" },
      { id: "5yearincrements.POP20C10", label: "2010 Population Age 20-24" },
      { id: "5yearincrements.POP25C10", label: "2010 Population Age 25-29" },
      { id: "5yearincrements.POP30C10", label: "2010 Population Age 30-34" },
      { id: "5yearincrements.POP35C10", label: "2010 Population Age 35-39" },
      { id: "5yearincrements.POP40C10", label: "2010 Population Age 40-44" },
      { id: "5yearincrements.POP45C10", label: "2010 Population Age 45-49" },
      { id: "5yearincrements.POP50C10", label: "2010 Population Age 50-54" },
      { id: "5yearincrements.POP55C10", label: "2010 Population Age 55-59" },
      { id: "5yearincrements.POP60C10", label: "2010 Population Age 60-64" },
      { id: "5yearincrements.POP65C10", label: "2010 Population Age 65-69" },
      { id: "5yearincrements.POP70C10", label: "2010 Population Age 70-74" },
      { id: "5yearincrements.POP75C10", label: "2010 Population Age 75-79" },
      { id: "5yearincrements.POP80C10", label: "2010 Population Age 80-84" },
      { id: "5yearincrements.POP85C10", label: "2010 Population Age 85+" },
      { id: "5yearincrements.MEDAGE10", label: "2010 Median Age" },

      // Historical (2010) Households
      { id: "householdsbysize.FAM2PERS10", label: "2010 Family HHs: 2-Person" },
      { id: "householdsbysize.NF1PERS10", label: "2010 Nonfamily HHs: 1-Person" },
      { id: "householdsbysize.NF2PERS10", label: "2010 Nonfamily HHs: 2-Person" },
    ]
  },
  Tapestry: {
    label: "Tapestry Variables",
    variables: [
      // Tapestry Households - 2025 Variables (corrected case and mapping)
      { id: "TapestryHouseholds.THHSNUM", label: "2025 Total HHs in Tapestry" },
      { id: "TapestryHouseholds.THHSA1", label: "2025 HHs In Tapestry Seg A1" },
      { id: "TapestryHouseholds.THHSA2", label: "2025 HHs In Tapestry Seg A2" },
      { id: "TapestryHouseholds.THHSA3", label: "2025 HHs In Tapestry Seg A3" },
      { id: "TapestryHouseholds.THHSA4", label: "2025 HHs In Tapestry Seg A4" },
      { id: "TapestryHouseholds.THHSA5", label: "2025 HHs In Tapestry Seg A5" },
      { id: "TapestryHouseholds.THHSA6", label: "2025 HHs In Tapestry Seg A6" },
      { id: "TapestryHouseholds.THHSB1", label: "2025 HHs In Tapestry Seg B1" },
      { id: "TapestryHouseholds.THHSB2", label: "2025 HHs In Tapestry Seg B2" },
      { id: "TapestryHouseholds.THHSB3", label: "2025 HHs In Tapestry Seg B3" },
      { id: "TapestryHouseholds.THHSC1", label: "2025 HHs In Tapestry Seg C1" },
      { id: "TapestryHouseholds.THHSC2", label: "2025 HHs In Tapestry Seg C2" },
      { id: "TapestryHouseholds.THHSC3", label: "2025 HHs In Tapestry Seg C3" },
      { id: "TapestryHouseholds.THHSC4", label: "2025 HHs In Tapestry Seg C4" },
      { id: "TapestryHouseholds.THHSC5", label: "2025 HHs In Tapestry Seg C5" },
      { id: "TapestryHouseholds.THHSC6", label: "2025 HHs In Tapestry Seg C6" },
      { id: "TapestryHouseholds.THHSD1", label: "2025 HHs In Tapestry Seg D1" },
      { id: "TapestryHouseholds.THHSD2", label: "2025 HHs In Tapestry Seg D2" },
      { id: "TapestryHouseholds.THHSD3", label: "2025 HHs In Tapestry Seg D3" },
      { id: "TapestryHouseholds.THHSD4", label: "2025 HHs In Tapestry Seg D4" },
      { id: "TapestryHouseholds.THHSD5", label: "2025 HHs In Tapestry Seg D5" },
      { id: "TapestryHouseholds.THHSE1", label: "2025 HHs In Tapestry Seg E1" },
      { id: "TapestryHouseholds.THHSE2", label: "2025 HHs In Tapestry Seg E2" },
      { id: "TapestryHouseholds.THHSE3", label: "2025 HHs In Tapestry Seg E3" },
      { id: "TapestryHouseholds.THHSE4", label: "2025 HHs In Tapestry Seg E4" },
      { id: "TapestryHouseholds.THHSE5", label: "2025 HHs In Tapestry Seg E5" },
      { id: "TapestryHouseholds.THHSE6", label: "2025 HHs In Tapestry Seg E6" },
      { id: "TapestryHouseholds.THHSF1", label: "2025 HHs In Tapestry Seg F1" },
      { id: "TapestryHouseholds.THHSF2", label: "2025 HHs In Tapestry Seg F2" },
      { id: "TapestryHouseholds.THHSF3", label: "2025 HHs In Tapestry Seg F3" },
      { id: "TapestryHouseholds.THHSF4", label: "2025 HHs In Tapestry Seg F4" },
      { id: "TapestryHouseholds.THHSF5", label: "2025 HHs In Tapestry Seg F5" },
      { id: "TapestryHouseholds.THHSG1", label: "2025 HHs In Tapestry Seg G1" },
      { id: "TapestryHouseholds.THHSG2", label: "2025 HHs In Tapestry Seg G2" },
      { id: "TapestryHouseholds.THHSG3", label: "2025 HHs In Tapestry Seg G3" },
      { id: "TapestryHouseholds.THHSH1", label: "2025 HHs In Tapestry Seg H1" },
      { id: "TapestryHouseholds.THHSH2", label: "2025 HHs In Tapestry Seg H2" },
      { id: "TapestryHouseholds.THHSH3", label: "2025 HHs In Tapestry Seg H3" },
      { id: "TapestryHouseholds.THHSH4", label: "2025 HHs In Tapestry Seg H4" },
      { id: "TapestryHouseholds.THHSI1", label: "2025 HHs In Tapestry Seg I1" },
      { id: "TapestryHouseholds.THHSI2", label: "2025 HHs In Tapestry Seg I2" },
      { id: "TapestryHouseholds.THHSI3", label: "2025 HHs In Tapestry Seg I3" },
      { id: "TapestryHouseholds.THHSI4", label: "2025 HHs In Tapestry Seg I4" },
      { id: "TapestryHouseholds.THHSI5", label: "2025 HHs In Tapestry Seg I5" },
      { id: "TapestryHouseholds.THHSI6", label: "2025 HHs In Tapestry Seg I6" },
      { id: "TapestryHouseholds.THHSI7", label: "2025 HHs In Tapestry Seg I7" },
      { id: "TapestryHouseholds.THHSJ1", label: "2025 HHs In Tapestry Seg J1" },
      { id: "TapestryHouseholds.THHSJ2", label: "2025 HHs In Tapestry Seg J2" },
      { id: "TapestryHouseholds.THHSJ3", label: "2025 HHs In Tapestry Seg J3" },
      { id: "TapestryHouseholds.THHSJ4", label: "2025 HHs In Tapestry Seg J4" },
      { id: "TapestryHouseholds.THHSK1", label: "2025 HHs In Tapestry Seg K1" },
      { id: "TapestryHouseholds.THHSK2", label: "2025 HHs In Tapestry Seg K2" },
      { id: "TapestryHouseholds.THHSK3", label: "2025 HHs In Tapestry Seg K3" },
      { id: "TapestryHouseholds.THHSK4", label: "2025 HHs In Tapestry Seg K4" },
      { id: "TapestryHouseholds.THHSK5", label: "2025 HHs In Tapestry Seg K5" },
      { id: "TapestryHouseholds.THHSK6", label: "2025 HHs In Tapestry Seg K6" },
      { id: "TapestryHouseholds.THHSK7", label: "2025 HHs In Tapestry Seg K7" },
      { id: "TapestryHouseholds.THHSK8", label: "2025 HHs In Tapestry Seg K8" },
      { id: "TapestryHouseholds.THHSL1", label: "2025 HHs In Tapestry Seg L1" },
      { id: "TapestryHouseholds.THHSL2", label: "2025 HHs In Tapestry Seg L2" },
      { id: "TapestryHouseholds.THHSL3", label: "2025 HHs In Tapestry Seg L3" },
      { id: "TapestryHouseholds.THHSUN", label: "2025 HHs in Tapestry Seg Unclassified" }
    ]
  },
  tier2: {
    label: "Tier 2 Variables",
    variables: [
      // Current Year 1-Year Age Increments (2025) - positions 0-83
      { id: "1yearincrements.AGE0_CY", label: "2025 Population Age <1" },
      { id: "1yearincrements.AGE1_CY", label: "2025 Population Age 1" },
      { id: "1yearincrements.AGE2_CY", label: "2025 Population Age 2" },
      { id: "1yearincrements.AGE3_CY", label: "2025 Population Age 3" },
      { id: "1yearincrements.AGE4_CY", label: "2025 Population Age 4" },
      { id: "1yearincrements.AGE5_CY", label: "2025 Population Age 5" },
      { id: "1yearincrements.AGE6_CY", label: "2025 Population Age 6" },
      { id: "1yearincrements.AGE7_CY", label: "2025 Population Age 7" },
      { id: "1yearincrements.AGE8_CY", label: "2025 Population Age 8" },
      { id: "1yearincrements.AGE9_CY", label: "2025 Population Age 9" },
      { id: "1yearincrements.AGE10_CY", label: "2025 Population Age 10" },
      { id: "1yearincrements.AGE11_CY", label: "2025 Population Age 11" },
      { id: "1yearincrements.AGE12_CY", label: "2025 Population Age 12" },
      { id: "1yearincrements.AGE13_CY", label: "2025 Population Age 13" },
      { id: "1yearincrements.AGE14_CY", label: "2025 Population Age 14" },
      { id: "1yearincrements.AGE15_CY", label: "2025 Population Age 15" },
      { id: "1yearincrements.AGE16_CY", label: "2025 Population Age 16" },
      { id: "1yearincrements.AGE17_CY", label: "2025 Population Age 17" },
      { id: "1yearincrements.AGE18_CY", label: "2025 Population Age 18" },
      { id: "1yearincrements.AGE19_CY", label: "2025 Population Age 19" },
      { id: "1yearincrements.AGE20_CY", label: "2025 Population Age 20" },
      { id: "1yearincrements.AGE21_CY", label: "2025 Population Age 21" },
      { id: "1yearincrements.AGE22_CY", label: "2025 Population Age 22" },
      { id: "1yearincrements.AGE23_CY", label: "2025 Population Age 23" },
      { id: "1yearincrements.AGE24_CY", label: "2025 Population Age 24" },
      { id: "1yearincrements.AGE25_CY", label: "2025 Population Age 25" },
      { id: "1yearincrements.AGE26_CY", label: "2025 Population Age 26" },
      { id: "1yearincrements.AGE27_CY", label: "2025 Population Age 27" },
      { id: "1yearincrements.AGE28_CY", label: "2025 Population Age 28" },
      { id: "1yearincrements.AGE29_CY", label: "2025 Population Age 29" },
      { id: "1yearincrements.AGE30_CY", label: "2025 Population Age 30" },
      { id: "1yearincrements.AGE31_CY", label: "2025 Population Age 31" },
      { id: "1yearincrements.AGE32_CY", label: "2025 Population Age 32" },
      { id: "1yearincrements.AGE33_CY", label: "2025 Population Age 33" },
      { id: "1yearincrements.AGE34_CY", label: "2025 Population Age 34" },
      { id: "1yearincrements.AGE35_CY", label: "2025 Population Age 35" },
      { id: "1yearincrements.AGE36_CY", label: "2025 Population Age 36" },
      { id: "1yearincrements.AGE37_CY", label: "2025 Population Age 37" },
      { id: "1yearincrements.AGE38_CY", label: "2025 Population Age 38" },
      { id: "1yearincrements.AGE39_CY", label: "2025 Population Age 39" },
      { id: "1yearincrements.AGE40_CY", label: "2025 Population Age 40" },
      { id: "1yearincrements.AGE41_CY", label: "2025 Population Age 41" },
      { id: "1yearincrements.AGE42_CY", label: "2025 Population Age 42" },
      { id: "1yearincrements.AGE43_CY", label: "2025 Population Age 43" },
      { id: "1yearincrements.AGE44_CY", label: "2025 Population Age 44" },
      { id: "1yearincrements.AGE45_CY", label: "2025 Population Age 45" },
      { id: "1yearincrements.AGE46_CY", label: "2025 Population Age 46" },
      { id: "1yearincrements.AGE47_CY", label: "2025 Population Age 47" },
      { id: "1yearincrements.AGE48_CY", label: "2025 Population Age 48" },
      { id: "1yearincrements.AGE49_CY", label: "2025 Population Age 49" },
      { id: "1yearincrements.AGE50_CY", label: "2025 Population Age 50" },
      { id: "1yearincrements.AGE51_CY", label: "2025 Population Age 51" },
      { id: "1yearincrements.AGE52_CY", label: "2025 Population Age 52" },
      { id: "1yearincrements.AGE53_CY", label: "2025 Population Age 53" },
      { id: "1yearincrements.AGE54_CY", label: "2025 Population Age 54" },
      { id: "1yearincrements.AGE55_CY", label: "2025 Population Age 55" },
      { id: "1yearincrements.AGE56_CY", label: "2025 Population Age 56" },
      { id: "1yearincrements.AGE57_CY", label: "2025 Population Age 57" },
      { id: "1yearincrements.AGE58_CY", label: "2025 Population Age 58" },
      { id: "1yearincrements.AGE59_CY", label: "2025 Population Age 59" },
      { id: "1yearincrements.AGE60_CY", label: "2025 Population Age 60" },
      { id: "1yearincrements.AGE61_CY", label: "2025 Population Age 61" },
      { id: "1yearincrements.AGE62_CY", label: "2025 Population Age 62" },
      { id: "1yearincrements.AGE63_CY", label: "2025 Population Age 63" },
      { id: "1yearincrements.AGE64_CY", label: "2025 Population Age 64" },
      { id: "1yearincrements.AGE65_CY", label: "2025 Population Age 65" },
      { id: "1yearincrements.AGE66_CY", label: "2025 Population Age 66" },
      { id: "1yearincrements.AGE67_CY", label: "2025 Population Age 67" },
      { id: "1yearincrements.AGE68_CY", label: "2025 Population Age 68" },
      { id: "1yearincrements.AGE69_CY", label: "2025 Population Age 69" },
      { id: "1yearincrements.AGE70_CY", label: "2025 Population Age 70" },
      { id: "1yearincrements.AGE71_CY", label: "2025 Population Age 71" },
      { id: "1yearincrements.AGE72_CY", label: "2025 Population Age 72" },
      { id: "1yearincrements.AGE73_CY", label: "2025 Population Age 73" },
      { id: "1yearincrements.AGE74_CY", label: "2025 Population Age 74" },
      { id: "1yearincrements.AGE75_CY", label: "2025 Population Age 75" },
      { id: "1yearincrements.AGE76_CY", label: "2025 Population Age 76" },
      { id: "1yearincrements.AGE77_CY", label: "2025 Population Age 77" },
      { id: "1yearincrements.AGE78_CY", label: "2025 Population Age 78" },
      { id: "1yearincrements.AGE79_CY", label: "2025 Population Age 79" },
      { id: "1yearincrements.AGE80_CY", label: "2025 Population Age 80" },
      { id: "1yearincrements.AGE81_CY", label: "2025 Population Age 81" },
      { id: "1yearincrements.AGE82_CY", label: "2025 Population Age 82" },
      { id: "1yearincrements.AGE83_CY", label: "2025 Population Age 83" },
      { id: "1yearincrements.AGE84_CY", label: "2025 Population Age 84" },

      // Future Year 1-Year Age Increments (2030) - positions 84-167
      { id: "1yearincrements.AGE0_FY", label: "2030 Population Age <1" },
      { id: "1yearincrements.AGE1_FY", label: "2030 Population Age 1" },
      { id: "1yearincrements.AGE2_FY", label: "2030 Population Age 2" },
      { id: "1yearincrements.AGE3_FY", label: "2030 Population Age 3" },
      { id: "1yearincrements.AGE4_FY", label: "2030 Population Age 4" },
      { id: "1yearincrements.AGE5_FY", label: "2030 Population Age 5" },
      { id: "1yearincrements.AGE6_FY", label: "2030 Population Age 6" },
      { id: "1yearincrements.AGE7_FY", label: "2030 Population Age 7" },
      { id: "1yearincrements.AGE8_FY", label: "2030 Population Age 8" },
      { id: "1yearincrements.AGE9_FY", label: "2030 Population Age 9" },
      { id: "1yearincrements.AGE10_FY", label: "2030 Population Age 10" },
      { id: "1yearincrements.AGE11_FY", label: "2030 Population Age 11" },
      { id: "1yearincrements.AGE12_FY", label: "2030 Population Age 12" },
      { id: "1yearincrements.AGE13_FY", label: "2030 Population Age 13" },
      { id: "1yearincrements.AGE14_FY", label: "2030 Population Age 14" },
      { id: "1yearincrements.AGE15_FY", label: "2030 Population Age 15" },
      { id: "1yearincrements.AGE16_FY", label: "2030 Population Age 16" },
      { id: "1yearincrements.AGE17_FY", label: "2030 Population Age 17" },
      { id: "1yearincrements.AGE18_FY", label: "2030 Population Age 18" },
      { id: "1yearincrements.AGE19_FY", label: "2030 Population Age 19" },
      { id: "1yearincrements.AGE20_FY", label: "2030 Population Age 20" },
      { id: "1yearincrements.AGE21_FY", label: "2030 Population Age 21" },
      { id: "1yearincrements.AGE22_FY", label: "2030 Population Age 22" },
      { id: "1yearincrements.AGE23_FY", label: "2030 Population Age 23" },
      { id: "1yearincrements.AGE24_FY", label: "2030 Population Age 24" },
      { id: "1yearincrements.AGE25_FY", label: "2030 Population Age 25" },
      { id: "1yearincrements.AGE26_FY", label: "2030 Population Age 26" },
      { id: "1yearincrements.AGE27_FY", label: "2030 Population Age 27" },
      { id: "1yearincrements.AGE28_FY", label: "2030 Population Age 28" },
      { id: "1yearincrements.AGE29_FY", label: "2030 Population Age 29" },
      { id: "1yearincrements.AGE30_FY", label: "2030 Population Age 30" },
      { id: "1yearincrements.AGE31_FY", label: "2030 Population Age 31" },
      { id: "1yearincrements.AGE32_FY", label: "2030 Population Age 32" },
      { id: "1yearincrements.AGE33_FY", label: "2030 Population Age 33" },
      { id: "1yearincrements.AGE34_FY", label: "2030 Population Age 34" },
      { id: "1yearincrements.AGE35_FY", label: "2030 Population Age 35" },
      { id: "1yearincrements.AGE36_FY", label: "2030 Population Age 36" },
      { id: "1yearincrements.AGE37_FY", label: "2030 Population Age 37" },
      { id: "1yearincrements.AGE38_FY", label: "2030 Population Age 38" },
      { id: "1yearincrements.AGE39_FY", label: "2030 Population Age 39" },
      { id: "1yearincrements.AGE40_FY", label: "2030 Population Age 40" },
      { id: "1yearincrements.AGE41_FY", label: "2030 Population Age 41" },
      { id: "1yearincrements.AGE42_FY", label: "2030 Population Age 42" },
      { id: "1yearincrements.AGE43_FY", label: "2030 Population Age 43" },
      { id: "1yearincrements.AGE44_FY", label: "2030 Population Age 44" },
      { id: "1yearincrements.AGE45_FY", label: "2030 Population Age 45" },
      { id: "1yearincrements.AGE46_FY", label: "2030 Population Age 46" },
      { id: "1yearincrements.AGE47_FY", label: "2030 Population Age 47" },
      { id: "1yearincrements.AGE48_FY", label: "2030 Population Age 48" },
      { id: "1yearincrements.AGE49_FY", label: "2030 Population Age 49" },
      { id: "1yearincrements.AGE50_FY", label: "2030 Population Age 50" },
      { id: "1yearincrements.AGE51_FY", label: "2030 Population Age 51" },
      { id: "1yearincrements.AGE52_FY", label: "2030 Population Age 52" },
      { id: "1yearincrements.AGE53_FY", label: "2030 Population Age 53" },
      { id: "1yearincrements.AGE54_FY", label: "2030 Population Age 54" },
      { id: "1yearincrements.AGE55_FY", label: "2030 Population Age 55" },
      { id: "1yearincrements.AGE56_FY", label: "2030 Population Age 56" },
      { id: "1yearincrements.AGE57_FY", label: "2030 Population Age 57" },
      { id: "1yearincrements.AGE58_FY", label: "2030 Population Age 58" },
      { id: "1yearincrements.AGE59_FY", label: "2030 Population Age 59" },
      { id: "1yearincrements.AGE60_FY", label: "2030 Population Age 60" },
      { id: "1yearincrements.AGE61_FY", label: "2030 Population Age 61" },
      { id: "1yearincrements.AGE62_FY", label: "2030 Population Age 62" },
      { id: "1yearincrements.AGE63_FY", label: "2030 Population Age 63" },
      { id: "1yearincrements.AGE64_FY", label: "2030 Population Age 64" },
      { id: "1yearincrements.AGE65_FY", label: "2030 Population Age 65" },
      { id: "1yearincrements.AGE66_FY", label: "2030 Population Age 66" },
      { id: "1yearincrements.AGE67_FY", label: "2030 Population Age 67" },
      { id: "1yearincrements.AGE68_FY", label: "2030 Population Age 68" },
      { id: "1yearincrements.AGE69_FY", label: "2030 Population Age 69" },
      { id: "1yearincrements.AGE70_FY", label: "2030 Population Age 70" },
      { id: "1yearincrements.AGE71_FY", label: "2030 Population Age 71" },
      { id: "1yearincrements.AGE72_FY", label: "2030 Population Age 72" },
      { id: "1yearincrements.AGE73_FY", label: "2030 Population Age 73" },
      { id: "1yearincrements.AGE74_FY", label: "2030 Population Age 74" },
      { id: "1yearincrements.AGE75_FY", label: "2030 Population Age 75" },
      { id: "1yearincrements.AGE76_FY", label: "2030 Population Age 76" },
      { id: "1yearincrements.AGE77_FY", label: "2030 Population Age 77" },
      { id: "1yearincrements.AGE78_FY", label: "2030 Population Age 78" },
      { id: "1yearincrements.AGE79_FY", label: "2030 Population Age 79" },
      { id: "1yearincrements.AGE80_FY", label: "2030 Population Age 80" },
      { id: "1yearincrements.AGE81_FY", label: "2030 Population Age 81" },
      { id: "1yearincrements.AGE82_FY", label: "2030 Population Age 82" },
      { id: "1yearincrements.AGE83_FY", label: "2030 Population Age 83" },
      { id: "1yearincrements.AGE84_FY", label: "2030 Population Age 84" },

      // Educational Attainment - positions 168-174
      { id: "educationalattainment.HSGRAD_CY", label: "2025 Pop Age 25+: High School Diploma" },
      { id: "educationalattainment.GED_CY", label: "2025 Pop Age 25+: GED" },
      { id: "educationalattainment.SMCOLL_CY", label: "2025 Pop Age 25+: Some College/No Degree" },
      { id: "educationalattainment.ASSCDEG_CY", label: "2025 Pop Age 25+: Associate's Degree" },
      { id: "educationalattainment.BACHDEG_CY", label: "2025 Pop Age 25+: Bachelor's Degree" },
      { id: "educationalattainment.GRADDEG_CY", label: "2025 Pop Age 25+: Grad/Professional Degree" },
      { id: "educationalattainment.EDUCBASECY", label: "2025 Educational Attainment Base" },

      // 2030 Income Base by Age Groups - positions 175-181
      { id: "incomebyage.IA15BASEFY", label: "2030 HH Income Base: HHr 15-24" },
      { id: "incomebyage.IA25BASEFY", label: "2030 HH Income Base: HHr 25-34" },
      { id: "incomebyage.IA35BASEFY", label: "2030 HH Income Base: HHr 35-44" },
      { id: "incomebyage.IA45BASEFY", label: "2030 HH Income Base: HHr 45-54" },
      { id: "incomebyage.IA55BASEFY", label: "2030 HH Income Base: HHr 55-64" },
      { id: "incomebyage.IA65BASEFY", label: "2030 HH Income Base: HHr 65-74" },
      { id: "incomebyage.IA75BASEFY", label: "2030 HH Income Base: HHr 75+" },

      // 2025 Median Household Income by Age Groups - positions 182-188
      { id: "incomebyage.MEDIA15_CY", label: "2025 Median HH Inc: HHr 15-24" },
      { id: "incomebyage.MEDIA25_CY", label: "2025 Median HH Inc: HHr 25-34" },
      { id: "incomebyage.MEDIA35_CY", label: "2025 Median HH Inc: HHr 35-44" },
      { id: "incomebyage.MEDIA45_CY", label: "2025 Median HH Inc: HHr 45-54" },
      { id: "incomebyage.MEDIA55_CY", label: "2025 Median HH Inc: HHr 55-64" },
      { id: "incomebyage.MEDIA65_CY", label: "2025 Median HH Inc: HHr 65-74" },
      { id: "incomebyage.MEDIA75_CY", label: "2025 Median HH Inc: HHr 75+" },

      // 2025 Income by Age Group - HHr 15-24 - positions 189-197
      { id: "incomebyage.A15I0_CY", label: "2025 HH Inc <$15000/HHr 15-24" },
      { id: "incomebyage.A15I15_CY", label: "2025 HH Inc $15K-24999/HHr 15-24" },
      { id: "incomebyage.A15I25_CY", label: "2025 HH Inc $25K-34999/HHr 15-24" },
      { id: "incomebyage.A15I35_CY", label: "2025 HH Inc $35K-49999/HHr 15-24" },
      { id: "incomebyage.A15I50_CY", label: "2025 HH Inc $50K-74999/HHr 15-24" },
      { id: "incomebyage.A15I75_CY", label: "2025 HH Inc $75K-99999/HHr 15-24" },
      { id: "incomebyage.A15I100_CY", label: "2025 HH Inc 100K-149999/HHr 15-24" },
      { id: "incomebyage.A15I150_CY", label: "2025 HH Inc 150K-199999/HHr 15-24" },
      { id: "incomebyage.A15I200_CY", label: "2025 HH Inc $200000+/HHr 15-24" },

      // 2025 Income by Age Group - HHr 55-64 - positions 198-206
      { id: "incomebyage.A55I0_CY", label: "2025 HH Inc <$15000/HHr 55-64" },
      { id: "incomebyage.A55I15_CY", label: "2025 HH Inc $15K-24999/HHr 55-64" },
      { id: "incomebyage.A55I25_CY", label: "2025 HH Inc $25K-34999/HHr 55-64" },
      { id: "incomebyage.A55I35_CY", label: "2025 HH Inc $35K-49999/HHr 55-64" },
      { id: "incomebyage.A55I50_CY", label: "2025 HH Inc $50K-74999/HHr 55-64" },
      { id: "incomebyage.A55I75_CY", label: "2025 HH Inc $75K-99999/HHr 55-64" },
      { id: "incomebyage.A55I100_CY", label: "2025 HH Inc 100K-149999/HHr 55-64" },
      { id: "incomebyage.A55I150_CY", label: "2025 HH Inc 150K-199999/HHr 55-64" },
      { id: "incomebyage.A55I200_CY", label: "2025 HH Inc $200000+/HHr 55-64" },

      // 2025 Income by Age Group - HHr 65-74 - positions 207-215
      { id: "incomebyage.A65I0_CY", label: "2025 HH Inc <$15000/HHr 65-74" },
      { id: "incomebyage.A65I15_CY", label: "2025 HH Inc $15K-24999/HHr 65-74" },
      { id: "incomebyage.A65I25_CY", label: "2025 HH Inc $25K-34999/HHr 65-74" },
      { id: "incomebyage.A65I35_CY", label: "2025 HH Inc $35K-49999/HHr 65-74" },
      { id: "incomebyage.A65I50_CY", label: "2025 HH Inc $50K-74999/HHr 65-74" },
      { id: "incomebyage.A65I75_CY", label: "2025 HH Inc $75K-99999/HHr 65-74" },
      { id: "incomebyage.A65I100_CY", label: "2025 HH Inc 100K-149999/HHr 65-74" },
      { id: "incomebyage.A65I150_CY", label: "2025 HH Inc 150K-199999/HHr 65-74" },
      { id: "incomebyage.A65I200_CY", label: "2025 HH Inc $200000+/HHr 65-74" },

      // 2025 Income by Age Group - HHr 75+ - positions 216-224
      { id: "incomebyage.A75I0_CY", label: "2025 HH Inc <$15000/HHr 75+" },
      { id: "incomebyage.A75I15_CY", label: "2025 HH Inc $15K-24999/HHr 75+" },
      { id: "incomebyage.A75I25_CY", label: "2025 HH Inc $25K-34999/HHr 75+" },
      { id: "incomebyage.A75I35_CY", label: "2025 HH Inc $35K-49999/HHr 75+" },
      { id: "incomebyage.A75I50_CY", label: "2025 HH Inc $50K-74999/HHr 75+" },
      { id: "incomebyage.A75I75_CY", label: "2025 HH Inc $75K-99999/HHr 75+" },
      { id: "incomebyage.A75I100_CY", label: "2025 HH Inc 100K-149999/HHr 75+" },
      { id: "incomebyage.A75I150_CY", label: "2025 HH Inc 150K-199999/HHr 75+" },
      { id: "incomebyage.A75I200_CY", label: "2025 HH Inc $200000+/HHr 75+" },

      // 2025 Net Worth by Age Groups - positions 225-231
      { id: "networth.MEDNWA15CY", label: "2025 Median Net Worth: HHr 15-24" },
      { id: "networth.MEDNWA25CY", label: "2025 Median Net Worth: HHr 25-34" },
      { id: "networth.MEDNWA35CY", label: "2025 Median Net Worth: HHr 35-44" },
      { id: "networth.MEDNWA45CY", label: "2025 Median Net Worth: HHr 45-54" },
      { id: "networth.MEDNWA55CY", label: "2025 Median Net Worth: HHr 55-64" },
      { id: "networth.MEDNWA65CY", label: "2025 Median Net Worth: HHr 65-74" },
      { id: "networth.MEDNWA75CY", label: "2025 Median Net Worth: HHr 75+" },

      // 2025 High Net Worth ($1M+) by Age Groups - positions 232-238
      { id: "networth.A15NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 15-24" },
      { id: "networth.A25NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 25-34" },
      { id: "networth.A35NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 35-44" },
      { id: "networth.A45NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 45-54" },
      { id: "networth.A55NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 55-64" },
      { id: "networth.A65NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 65-74" },
      { id: "networth.A75NW1M_CY", label: "2025 HH Net Worth $1000000+/HHr 75+" },

      // 2030 Income by Age Groups - positions start at 239
      // 2030 Income by Age Group - HHr 15-24
      { id: "incomebyage.A15I0_FY", label: "2030 HH Inc <$15000/HHr 15-24" },
      { id: "incomebyage.A15I15_FY", label: "2030 HH Inc $15K-24999/HHr 15-24" },
      { id: "incomebyage.A15I25_FY", label: "2030 HH Inc $25K-34999/HHr 15-24" },
      { id: "incomebyage.A15I35_FY", label: "2030 HH Inc $35K-49999/HHr 15-24" },
      { id: "incomebyage.A15I50_FY", label: "2030 HH Inc $50K-74999/HHr 15-24" },
      { id: "incomebyage.A15I75_FY", label: "2030 HH Inc $75K-99999/HHr 15-24" },
      { id: "incomebyage.A15I100_FY", label: "2030 HH Inc 100K-149999/HHr 15-24" },
      { id: "incomebyage.A15I150_FY", label: "2030 HH Inc 150K-199999/HHr 15-24" },
      { id: "incomebyage.A15I200_FY", label: "2030 HH Inc $200000+/HHr 15-24" },

      // 2030 Income by Age Group - HHr 25-34
      { id: "incomebyage.A25I0_FY", label: "2030 HH Inc <$15000/HHr 25-34" },
      { id: "incomebyage.A25I15_FY", label: "2030 HH Inc $15K-24999/HHr 25-34" },
      { id: "incomebyage.A25I25_FY", label: "2030 HH Inc $25K-34999/HHr 25-34" },
      { id: "incomebyage.A25I35_FY", label: "2030 HH Inc $35K-49999/HHr 25-34" },
      { id: "incomebyage.A25I50_FY", label: "2030 HH Inc $50K-74999/HHr 25-34" },
      { id: "incomebyage.A25I75_FY", label: "2030 HH Inc $75K-99999/HHr 25-34" },
      { id: "incomebyage.A25I100_FY", label: "2030 HH Inc 100K-149999/HHr 25-34" },
      { id: "incomebyage.A25I150_FY", label: "2030 HH Inc 150K-199999/HHr 25-34" },
      { id: "incomebyage.A25I200_FY", label: "2030 HH Inc $200000+/HHr 25-34" },

      // 2030 Income by Age Group - HHr 35-44
      { id: "incomebyage.A35I0_FY", label: "2030 HH Inc <$15000/HHr 35-44" },
      { id: "incomebyage.A35I15_FY", label: "2030 HH Inc $15K-24999/HHr 35-44" },
      { id: "incomebyage.A35I25_FY", label: "2030 HH Inc $25K-34999/HHr 35-44" },
      { id: "incomebyage.A35I35_FY", label: "2030 HH Inc $35K-49999/HHr 35-44" },
      { id: "incomebyage.A35I50_FY", label: "2030 HH Inc $50K-74999/HHr 35-44" },
      { id: "incomebyage.A35I75_FY", label: "2030 HH Inc $75K-99999/HHr 35-44" },
      { id: "incomebyage.A35I100_FY", label: "2030 HH Inc 100K-149999/HHr 35-44" },
      { id: "incomebyage.A35I150_FY", label: "2030 HH Inc 150K-199999/HHr 35-44" },
      { id: "incomebyage.A35I200_FY", label: "2030 HH Inc $200000+/HHr 35-44" },

      // 2030 Income by Age Group - HHr 45-54
      { id: "incomebyage.A45I0_FY", label: "2030 HH Inc <$15000/HHr 45-54" },
      { id: "incomebyage.A45I15_FY", label: "2030 HH Inc $15K-24999/HHr 45-54" },
      { id: "incomebyage.A45I25_FY", label: "2030 HH Inc $25K-34999/HHr 45-54" },
      { id: "incomebyage.A45I35_FY", label: "2030 HH Inc $35K-49999/HHr 45-54" },
      { id: "incomebyage.A45I50_FY", label: "2030 HH Inc $50K-74999/HHr 45-54" },
      { id: "incomebyage.A45I75_FY", label: "2030 HH Inc $75K-99999/HHr 45-54" },
      { id: "incomebyage.A45I100_FY", label: "2030 HH Inc 100K-149999/HHr 45-54" },
      { id: "incomebyage.A45I150_FY", label: "2030 HH Inc 150K-199999/HHr 45-54" },
      { id: "incomebyage.A45I200_FY", label: "2030 HH Inc $200000+/HHr 45-54" },

      // 2030 Income by Age Group - HHr 55-64
      { id: "incomebyage.A55I0_FY", label: "2030 HH Inc <$15000/HHr 55-64" },
      { id: "incomebyage.A55I15_FY", label: "2030 HH Inc $15K-24999/HHr 55-64" },
      { id: "incomebyage.A55I25_FY", label: "2030 HH Inc $25K-34999/HHr 55-64" },
      { id: "incomebyage.A55I35_FY", label: "2030 HH Inc $35K-49999/HHr 55-64" },
      { id: "incomebyage.A55I50_FY", label: "2030 HH Inc $50K-74999/HHr 55-64" },
      { id: "incomebyage.A55I75_FY", label: "2030 HH Inc $75K-99999/HHr 55-64" },
      { id: "incomebyage.A55I100_FY", label: "2030 HH Inc 100K-149999/HHr 55-64" },
      { id: "incomebyage.A55I150_FY", label: "2030 HH Inc 150K-199999/HHr 55-64" },
      { id: "incomebyage.A55I200_FY", label: "2030 HH Inc $200000+/HHr 55-64" },

      // 2030 Income by Age Group - HHr 65-74
      { id: "incomebyage.A65I0_FY", label: "2030 HH Inc <$15000/HHr 65-74" },
      { id: "incomebyage.A65I15_FY", label: "2030 HH Inc $15K-24999/HHr 65-74" },
      { id: "incomebyage.A65I25_FY", label: "2030 HH Inc $25K-34999/HHr 65-74" },
      { id: "incomebyage.A65I35_FY", label: "2030 HH Inc $35K-49999/HHr 65-74" },
      { id: "incomebyage.A65I50_FY", label: "2030 HH Inc $50K-74999/HHr 65-74" },
      { id: "incomebyage.A65I75_FY", label: "2030 HH Inc $75K-99999/HHr 65-74" },
      { id: "incomebyage.A65I100_FY", label: "2030 HH Inc 100K-149999/HHr 65-74" },
      { id: "incomebyage.A65I150_FY", label: "2030 HH Inc 150K-199999/HHr 65-74" },
      { id: "incomebyage.A65I200_FY", label: "2030 HH Inc $200000+/HHr 65-74" },

      // 2030 Income by Age Group - HHr 75+
      { id: "incomebyage.A75I0_FY", label: "2030 HH Inc <$15000/HHr 75+" },
      { id: "incomebyage.A75I15_FY", label: "2030 HH Inc $15K-24999/HHr 75+" },
      { id: "incomebyage.A75I25_FY", label: "2030 HH Inc $25K-34999/HHr 75+" },
      { id: "incomebyage.A75I35_FY", label: "2030 HH Inc $35K-49999/HHr 75+" },
      { id: "incomebyage.A75I50_FY", label: "2030 HH Inc $50K-74999/HHr 75+" },
      { id: "incomebyage.A75I75_FY", label: "2030 HH Inc $75K-99999/HHr 75+" },
      { id: "incomebyage.A75I100_FY", label: "2030 HH Inc 100K-149999/HHr 75+" },
      { id: "incomebyage.A75I150_FY", label: "2030 HH Inc 150K-199999/HHr 75+" },
      { id: "incomebyage.A75I200_FY", label: "2030 HH Inc $200000+/HHr 75+" },

      // 2010 Household Composition Data - positions start at 302
      { id: "householdsbysize.FAM2PERS10", label: "2010 Family HHs: 2-Person" },
      { id: "householdsbysize.FAM3PERS10", label: "2010 Family HHs: 3-Person" },
      { id: "householdsbysize.FAM4PERS10", label: "2010 Family HHs: 4-Person" },
      { id: "householdsbysize.FAM5PERS10", label: "2010 Family HHs: 5-Person" },
      { id: "householdsbysize.FAM6PERS10", label: "2010 Family HHs: 6-Person" },
      { id: "householdsbysize.FAM7PERS10", label: "2010 Family HHs: 7+-Person" },
      { id: "householdsbysize.NF1PERS10", label: "2010 Nonfamily HHs: 1-Person" },
      { id: "householdsbysize.NF2PERS10", label: "2010 Nonfamily HHs: 2-Person" },
      { id: "householdsbysize.NF3PERS10", label: "2010 Nonfamily HHs: 3-Person" },
      { id: "householdsbysize.NF4PERS10", label: "2010 Nonfamily HHs: 4-Person" },
      { id: "householdsbysize.NF5PERS10", label: "2010 Nonfamily HHs: 5-Person" },
      { id: "householdsbysize.NF6PERS10", label: "2010 Nonfamily HHs: 6-Person" },
      { id: "householdsbysize.NF7PERS10", label: "2010 Nonfamily HHs: 7+-Person" },

      // 2025 Average Household Income by Age Groups - positions start at 315
      { id: "incomebyage.AVGIA15_CY", label: "2025 Average HH Inc: HHr 15-24" },
      { id: "incomebyage.AVGIA25_CY", label: "2025 Average HH Inc: HHr 25-34" },
      { id: "incomebyage.AVGIA35_CY", label: "2025 Average HH Inc: HHr 35-44" },
      { id: "incomebyage.AVGIA45_CY", label: "2025 Average HH Inc: HHr 45-54" },
      { id: "incomebyage.AVGIA55_CY", label: "2025 Average HH Inc: HHr 55-64" },
      { id: "incomebyage.AVGIA65_CY", label: "2025 Average HH Inc: HHr 65-74" },
      { id: "incomebyage.AVGIA75_CY", label: "2025 Average HH Inc: HHr 75+" },

      // 2030 Median Household Income by Age Groups - positions start at 322
      { id: "incomebyage.MEDIA15_FY", label: "2030 Median HH Inc: HHr 15-24" },
      { id: "incomebyage.MEDIA25_FY", label: "2030 Median HH Inc: HHr 25-34" },
      { id: "incomebyage.MEDIA35_FY", label: "2030 Median HH Inc: HHr 35-44" },
      { id: "incomebyage.MEDIA45_FY", label: "2030 Median HH Inc: HHr 45-54" },
      { id: "incomebyage.MEDIA55_FY", label: "2030 Median HH Inc: HHr 55-64" },
      { id: "incomebyage.MEDIA65_FY", label: "2030 Median HH Inc: HHr 65-74" },
      { id: "incomebyage.MEDIA55UFY", label: "2030 Median HH Inc: HHr 55+" },
      { id: "incomebyage.MEDIA65UFY", label: "2030 Median HH Inc: HHr 65+" },
      { id: "incomebyage.MEDIA75_FY", label: "2030 Median HH Inc: HHr 75+" },

      // 2025 Median Household Income - Additional Age Ranges - positions start at 331
      { id: "incomebyage.MEDIA55UCY", label: "2025 Median HH Inc: HHr 55+" },
      { id: "incomebyage.MEDIA65UCY", label: "2025 Median HH Inc: HHr 65+" },
      { id: "incomebyage.MEDIA75_CY", label: "2025 Median HH Inc: HHr 75+" },

      // Land Area and Population Density
      { id: "householdtotals.LANDAREA", label: "2025 Land Area in Square Miles" },
      { id: "populationtotals.POPDENS_CY", label: "2025 Population Density per Square Mile" }
    ]
  },
  retail: {
    label: "Retail Variables",
    variables: [
      { id: "HousingHousehold.X4043_A", label: "2025 Household Furnishings & Equipment (Average)" },
      { id: "entertainment.X9051_A", label: "2025 Sports/Rec/Exercise Equipment (Average)" },
      { id: "entertainment.X9024_A", label: "2025 Audio (Average)" },
      { id: "entertainment.X9065_A", label: "2025 Reading (Average)" },
      { id: "HousingHousehold.X4063_A", label: "2025 Major Appliances (Average)" },
      { id: "clothing.X5001_A", label: "2025 Apparel & Services (Average)" },
      { id: "clothing.X5002_A", label: "2025 Men`s Apparel (Average)" },
      { id: "clothing.X5016_A", label: "2025 Women`s Apparel (Average)" },
      { id: "clothing.X5032_A", label: "2025 Children`s Apparel (Average)" },
      { id: "clothing.X5063_A", label: "2025 Footwear (Average)" },
      { id: "food.X1131_A", label: "2025 Meals at Restaurants/Other (Average)" },
      { id: "food.X1156_A", label: "2025 Food and Nonalcoholic Beverages at Fast Food (Average)" },
      { id: "food.X1157_A", label: "2025 Food and Nonalcoholic Beverages at Full Service Restaurants (Average)" },
      { id: "food.X1130_A", label: "2025 Food Away from Home (Average)" },
      { id: "food.X2007_A", label: "2025 Alcoholic Beverages Away from Home (Average)" },
      { id: "SpendingTotal.X15001_A", label: "2025 Retail Goods (Average)" },
      { id: "entertainment.X9036_A", label: "2025 Pet Food (Average)" },
      { id: "entertainment.X9037_A", label: "2025 Pets/Pet Supplies/Medicine for Pets (Average)" },
      { id: "entertainment.X9038_A", label: "2025 Pet Services (Average)" },
      { id: "entertainment.X9039_A", label: "2025 Vet Services (Average)" },
      { id: "transportation.X6011_A", label: "2025 Gasoline (Average)" },
      { id: "transportation.X6015_A", label: "2025 Vehicle Maintenance & Repairs (Average)" },
      { id: "HousingHousehold.X4043_I", label: "2025 Household Furnishings & Equipment (Index)" },
      { id: "entertainment.X9051_I", label: "2025 Sports/Rec/Exercise Equipment (Index)" },
      { id: "entertainment.X9024_I", label: "2025 Audio (Index)" },
      { id: "entertainment.X9065_I", label: "2025 Reading (Index)" },
      { id: "HousingHousehold.X4063_I", label: "2025 Major Appliances (Index)" },
      { id: "clothing.X5001_I", label: "2025 Apparel & Services (Index)" },
      { id: "clothing.X5002_I", label: "2025 Men`s Apparel (Index)" },
      { id: "clothing.X5016_I", label: "2025 Women`s Apparel (Index)" },
      { id: "clothing.X5032_I", label: "2025 Children`s Apparel (Index)" },
      { id: "clothing.X5063_I", label: "2025 Footwear (Index)" },
      { id: "food.X1131_I", label: "2025 Meals at Restaurants/Other (Index)" },
      { id: "food.X1156_I", label: "2025 Food and Nonalcoholic Beverages at Fast Food (Index)" },
      { id: "food.X1157_I", label: "2025 Food and Nonalcoholic Beverages at Full Service Restaurants (Index)" },
      { id: "food.X1130_I", label: "2025 Food Away from Home (Index)" },
      { id: "food.X2007_I", label: "2025 Alcoholic Beverages Away from Home (Index)" },
      { id: "SpendingTotal.X15001_I", label: "2025 Retail Goods (Index)" },
      { id: "entertainment.X9036_I", label: "2025 Pet Food (Index)" },
      { id: "entertainment.X9037_I", label: "2025 Pets/Pet Supplies/Medicine for Pets (Index)" },
      { id: "entertainment.X9038_I", label: "2025 Pet Services (Index)" },
      { id: "entertainment.X9039_I", label: "2025 Vet Services (Index)" },
      { id: "transportation.X6011_I", label: "2025 Gasoline (Index)" },
      { id: "transportation.X6015_I", label: "2025 Vehicle Maintenance & Repairs (Index)" },
      { id: "HousingHousehold.X4043FY_A", label: "2030 Household Furnishings & Equipment (Average)" },
      { id: "entertainment.X9051FY_A", label: "2030 Sports/Rec/Exercise Equipment (Average)" },
      { id: "entertainment.X9024FY_A", label: "2030 Audio (Average)" },
      { id: "entertainment.X9065FY_A", label: "2030 Reading (Average)" },
      { id: "HousingHousehold.X4063FY_A", label: "2030 Major Appliances (Average)" },
      { id: "clothing.X5001FY_A", label: "2030 Apparel & Services (Average)" },
      { id: "clothing.X5002FY_A", label: "2030 Men`s Apparel (Average)" },
      { id: "clothing.X5016FY_A", label: "2030 Women`s Apparel (Average)" },
      { id: "clothing.X5032FY_A", label: "2030 Children`s Apparel (Average)" },
      { id: "clothing.X5063FY_A", label: "2030 Footwear (Average)" },
      { id: "food.X1131FY_A", label: "2030 Meals at Restaurants/Other (Average)" },
      { id: "food.X1156FY_A", label: "2030 Food and Nonalcoholic Beverages at Fast Food (Average)" },
      { id: "food.X1157FY_A", label: "2030 Food and Nonalcoholic Beverages at Full Service Restaurants (Average)" },
      { id: "food.X1130FY_A", label: "2030 Food Away from Home (Average)" },
      { id: "food.X2007FY_A", label: "2030 Alcoholic Beverages Away from Home (Average)" },
      { id: "SpendingTotal.X15001FY_A", label: "2030 Retail Goods (Average)" },
      { id: "entertainment.X9036FY_A", label: "2030 Pet Food (Average)" },
      { id: "entertainment.X9037FY_A", label: "2030 Pets/Pet Supplies/Medicine for Pets (Average)" },
      { id: "entertainment.X9038FY_A", label: "2030 Pet Services (Average)" },
      { id: "entertainment.X9039FY_A", label: "2030 Vet Services (Average)" },
      { id: "transportation.X6011FY_A", label: "2030 Gasoline (Average)" },
      { id: "transportation.X6015FY_A", label: "2030 Vehicle Maintenance & Repairs (Average)" },
      { id: "HousingHousehold.X4043FY_I", label: "2030 Household Furnishings & Equipment (Index)" },
      { id: "entertainment.X9051FY_I", label: "2030 Sports/Rec/Exercise Equipment (Index)" },
      { id: "entertainment.X9024FY_I", label: "2030 Audio (Index)" },
      { id: "entertainment.X9065FY_I", label: "2030 Reading (Index)" },
      { id: "HousingHousehold.X4063FY_I", label: "2030 Major Appliances (Index)" },
      { id: "clothing.X5001FY_I", label: "2030 Apparel & Services (Index)" },
      { id: "clothing.X5002FY_I", label: "2030 Men`s Apparel (Index)" },
      { id: "clothing.X5016FY_I", label: "2030 Women`s Apparel (Index)" },
      { id: "clothing.X5032FY_I", label: "2030 Children`s Apparel (Index)" },
      { id: "clothing.X5063FY_I", label: "2030 Footwear (Index)" },
      { id: "food.X1131FY_I", label: "2030 Meals at Restaurants/Other (Index)" },
      { id: "food.X1156FY_I", label: "2030 Food and Nonalcoholic Beverages at Fast Food (Index)" },
      { id: "food.X1157FY_I", label: "2030 Food and Nonalcoholic Beverages at Full Service Restaurants (Index)" },
      { id: "food.X1130FY_I", label: "2030 Food Away from Home (Index)" },
      { id: "food.X2007FY_I", label: "2030 Alcoholic Beverages Away from Home (Index)" },
      { id: "SpendingTotal.X15001FY_I", label: "2030 Retail Goods (Index)" },
      { id: "entertainment.X9036FY_I", label: "2030 Pet Food (Index)" },
      { id: "entertainment.X9037FY_I", label: "2030 Pets/Pet Supplies/Medicine for Pets (Index)" },
      { id: "entertainment.X9038FY_I", label: "2030 Pet Services (Index)" },
      { id: "entertainment.X9039FY_I", label: "2030 Vet Services (Index)" },
      { id: "transportation.X6011FY_I", label: "2030 Gasoline (Index)" },
      { id: "transportation.X6015FY_I", label: "2030 Vehicle Maintenance & Repairs (Index)" },
      { id: "shopping.MP31198a_I", label: "2025 Ordered Home Furnishing Online Last 6 Mo (Index)" },
      { id: "shopping.MP31191a_I", label: "2025 Ordered Fitness Apparel/Equipment Online Last 6 Mo (Index)" },
      { id: "shopping.MP31212a_I", label: "2025 Ordered Stereo/Audio Equipment Online Last 6 Mo (Index)" },
      { id: "shopping.MP31176a_I", label: "2025 Ordered Book Online Last 6 Mo (Index)" },
      { id: "shopping.MP31201a_I", label: "2025 Ordered Household/Small Appliance Online Last 6 Mo (Index)" },
      { id: "shopping.MP31181a_I", label: "2025 Ordered Clothing/Apparel Online Last 6 Mo (Index)" },
      { id: "shopping.MP31172a_I", label: "2025 Ordered Automotive Product Online Last 6 Mo (Index)" },
      { id: "shopping.MP31206a_I", label: "2025 Ordered Pet Products/Supplies Online Last 6 Mo (Index)" },
      { id: "PsychographicsShopping.MP28805a_I", label: "2025 OK Buying Items Like Cars/Appliances Online: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28806a_I", label: "2025 OK Buying Items Like Cars/Appliances Online: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28807a_I", label: "2025 OK Buying Items Like Cars/Appliances Online: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28808a_I", label: "2025 OK Buying Items Like Cars/Appliances Online: 4-Agree Completely (Index)" },
      { id: "PsychographicsShopping.MP28809a_I", label: "2025 Only Shop at a Few Online Stores: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28810a_I", label: "2025 Only Shop at a Few Online Stores: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28811a_I", label: "2025 Only Shop at a Few Online Stores: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28812a_I", label: "2025 Only Shop at a Few Online Stores: 4-Agree Completely (Index)" },
      { id: "PsychographicsShopping.MP28833a_I", label: "2025 Research Online Before Buy Locally: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28834a_I", label: "2025 Research Online Before Buy Locally: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28835a_I", label: "2025 Research Online Before Buy Locally: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28836a_I", label: "2025 Research Online Before Buy Locally: 4-Agree Completely (Index)" }
    ]
  },
  testVariables: {
    label: "Test Variables",
    variables: [
      // 2025 Household Income Variables
      { id: "householdincome.INC0_CY", label: "2025 HH Income <$10000" },
      { id: "householdincome.INC10_CY", label: "2025 HH Income $10000-14999" },
      { id: "householdincome.INC15_CY", label: "2025 HH Income $15000-19999" },
      { id: "householdincome.INC20_CY", label: "2025 HH Income $20000-24999" },
      { id: "householdincome.INC25_CY", label: "2025 HH Income $25000-29999" },
      { id: "householdincome.INC30_CY", label: "2025 HH Income $30000-34999" },
      { id: "householdincome.INC35_CY", label: "2025 HH Income $35000-39999" },
      { id: "householdincome.INC40_CY", label: "2025 HH Income $40000-44999" },
      { id: "householdincome.INC45_CY", label: "2025 HH Income $45000-49999" },
      { id: "householdincome.INC50_CY", label: "2025 HH Income $50000-59999" },
      { id: "householdincome.INC60_CY", label: "2025 HH Income $60000-74999" },
      { id: "householdincome.INC75_CY", label: "2025 HH Income $75000-99999" },
      { id: "householdincome.INC100_CY", label: "2025 HH Income $100000-124999" },
      { id: "householdincome.INC125_CY", label: "2025 HH Income $125000-149999" },
      { id: "householdincome.INC150_CY", label: "2025 HH Income $150000-199999" },
      { id: "householdincome.INC200_CY", label: "2025 HH Income $200000-249999" },
      { id: "householdincome.INC250_CY", label: "2025 HH Income $250000-299999" },
      { id: "householdincome.INC300_CY", label: "2025 HH Income $300000-399999" },
      { id: "householdincome.INC400_CY", label: "2025 HH Income $400000-499999" },
      { id: "householdincome.INC500_CY", label: "2025 HH Income $500000+" },
      
      // 2030 Household Income Variables
      { id: "householdincome.INC0_FY", label: "2030 HH Income <$10000" },
      { id: "householdincome.INC10_FY", label: "2030 HH Income $10000-14999" },
      { id: "householdincome.INC15_FY", label: "2030 HH Income $15000-19999" },
      { id: "householdincome.INC20_FY", label: "2030 HH Income $20000-24999" },
      { id: "householdincome.INC25_FY", label: "2030 HH Income $25000-29999" },
      { id: "householdincome.INC30_FY", label: "2030 HH Income $30000-34999" },
      { id: "householdincome.INC35_FY", label: "2030 HH Income $35000-39999" },
      { id: "householdincome.INC40_FY", label: "2030 HH Income $40000-44999" },
      { id: "householdincome.INC45_FY", label: "2030 HH Income $45000-49999" },
      { id: "householdincome.INC50_FY", label: "2030 HH Income $50000-59999" },
      { id: "householdincome.INC60_FY", label: "2030 HH Income $60000-74999" },
      { id: "householdincome.INC75_FY", label: "2030 HH Income $75000-99999" },
      { id: "householdincome.INC100_FY", label: "2030 HH Income $100000-124999" },
      { id: "householdincome.INC125_FY", label: "2030 HH Income $125000-149999" },
      { id: "householdincome.INC150_FY", label: "2030 HH Income $150000-199999" },
      { id: "householdincome.INC200_FY", label: "2030 HH Income $200000-249999" },
      { id: "householdincome.INC250_FY", label: "2030 HH Income $250000-299999" },
      { id: "householdincome.INC300_FY", label: "2030 HH Income $300000-399999" },
      { id: "householdincome.INC400_FY", label: "2030 HH Income $400000-499999" },
      { id: "householdincome.INC500_FY", label: "2030 HH Income $500000+" }
    ]
  }
};

export const analysisCategories2024 = {
  tier1: {
    label: "Core (Tier 1) Variables",
    variables: [
      // Current Year Population Base
      { id: "AtRisk.TOTPOP_CY", label: "2024 Total Population" },
      { id: "AtRisk.TOTHH_CY", label: "2024 Total Households" },
      { id: "AtRisk.AVGHHSZ_CY", label: "2024 Average Household Size" },

      // Current Year Population by Age
      { id: "5yearincrements.POP0_CY", label: "2024 Population Age 0-4" },
      { id: "5yearincrements.POP5_CY", label: "2024 Population Age 5-9" },
      { id: "5yearincrements.POP10_CY", label: "2024 Population Age 10-14" },
      { id: "5yearincrements.POP15_CY", label: "2024 Population Age 15-19" },
      { id: "5yearincrements.POP20_CY", label: "2024 Population Age 20-24" },
      { id: "5yearincrements.POP25_CY", label: "2024 Population Age 25-29" },
      { id: "5yearincrements.POP30_CY", label: "2024 Population Age 30-34" },
      { id: "5yearincrements.POP35_CY", label: "2024 Population Age 35-39" },
      { id: "5yearincrements.POP40_CY", label: "2024 Population Age 40-44" },
      { id: "5yearincrements.POP45_CY", label: "2024 Population Age 45-49" },
      { id: "5yearincrements.POP50_CY", label: "2024 Population Age 50-54" },
      { id: "5yearincrements.POP55_CY", label: "2024 Population Age 55-59" },
      { id: "5yearincrements.POP60_CY", label: "2024 Population Age 60-64" },
      { id: "5yearincrements.POP65_CY", label: "2024 Population Age 65-69" },
      { id: "5yearincrements.POP70_CY", label: "2024 Population Age 70-74" },
      { id: "5yearincrements.POP75_CY", label: "2024 Population Age 75-79" },
      { id: "5yearincrements.POP80_CY", label: "2024 Population Age 80-84" },
      { id: "5yearincrements.POP85_CY", label: "2024 Population Age 85+" },
      { id: "5yearincrements.MEDAGE_CY", label: "2024 Median Age" },

      // Current Year Daytime Population
      { id: "DaytimePopulation.DPOPWRK_CY", label: "2024 Daytime Pop: Workers" },

      // Current Year Household Income
      { id: "householdincome.HINC0_CY", label: "2024 HH Income <$15000" },
      { id: "householdincome.HINC15_CY", label: "2024 HH Income $15000-24999" },
      { id: "householdincome.HINC25_CY", label: "2024 HH Income $25000-34999" },
      { id: "householdincome.HINC35_CY", label: "2024 HH Income $35000-49999" },
      { id: "householdincome.HINC50_CY", label: "2024 HH Income $50000-74999" },
      { id: "householdincome.HINC75_CY", label: "2024 HH Income $75000-99999" },
      { id: "householdincome.HINC100_CY", label: "2024 HH Income $100000-149999" },
      { id: "householdincome.HINC150_CY", label: "2024 HH Income $150000-199999" },
      { id: "householdincome.HINC200_CY", label: "2024 HH Income $200000+" },
      { id: "householdincome.MEDHINC_CY", label: "2024 Median Household Income" },
      { id: "householdincome.AVGHINC_CY", label: "2024 Average Household Income" },

      // Current Year Income by Age Groups - Organized with base before details
      { id: "incomebyage.IA15BASECY", label: "2024 HH Income Base: HHr 15-24" },

      // Age 25-34 Income
      { id: "incomebyage.A25I0_CY", label: "2024 HH Inc <$15000/HHr 25-34" },
      { id: "incomebyage.A25I15_CY", label: "2024 HH Inc $15K-24999/HHr 25-34" },
      { id: "incomebyage.A25I25_CY", label: "2024 HH Inc $25K-34999/HHr 25-34" },
      { id: "incomebyage.A25I35_CY", label: "2024 HH Inc $35K-49999/HHr 25-34" },
      { id: "incomebyage.A25I50_CY", label: "2024 HH Inc $50K-74999/HHr 25-34" },
      { id: "incomebyage.A25I75_CY", label: "2024 HH Inc $75K-99999/HHr 25-34" },
      { id: "incomebyage.A25I100_CY", label: "2024 HH Inc 100K-149999/HHr 25-34" },
      { id: "incomebyage.A25I150_CY", label: "2024 HH Inc 150K-199999/HHr 25-34" },
      { id: "incomebyage.A25I200_CY", label: "2024 HH Inc $200000+/HHr 25-34" },
      { id: "incomebyage.IA25BASECY", label: "2024 HH Income Base: HHr 25-34" },

      // Age 35-44 Income
      { id: "incomebyage.A35I0_CY", label: "2024 HH Inc <$15000/HHr 35-44" },
      { id: "incomebyage.A35I15_CY", label: "2024 HH Inc $15K-24999/HHr 35-44" },
      { id: "incomebyage.A35I25_CY", label: "2024 HH Inc $25K-34999/HHr 35-44" },
      { id: "incomebyage.A35I35_CY", label: "2024 HH Inc $35K-49999/HHr 35-44" },
      { id: "incomebyage.A35I50_CY", label: "2024 HH Inc $50K-74999/HHr 35-44" },
      { id: "incomebyage.A35I75_CY", label: "2024 HH Inc $75K-99999/HHr 35-44" },
      { id: "incomebyage.A35I100_CY", label: "2024 HH Inc 100K-149999/HHr 35-44" },
      { id: "incomebyage.A35I150_CY", label: "2024 HH Inc 150K-199999/HHr 35-44" },
      { id: "incomebyage.A35I200_CY", label: "2024 HH Inc $200000+/HHr 35-44" },
      { id: "incomebyage.IA35BASECY", label: "2024 HH Income Base: HHr 35-44" },

      // Age 45-54 Income
      { id: "incomebyage.A45I0_CY", label: "2024 HH Inc <$15000/HHr 45-54" },
      { id: "incomebyage.A45I15_CY", label: "2024 HH Inc $15K-24999/HHr 45-54" },
      { id: "incomebyage.A45I25_CY", label: "2024 HH Inc $25K-34999/HHr 45-54" },
      { id: "incomebyage.A45I35_CY", label: "2024 HH Inc $35K-49999/HHr 45-54" },
      { id: "incomebyage.A45I50_CY", label: "2024 HH Inc $50K-74999/HHr 45-54" },
      { id: "incomebyage.A45I75_CY", label: "2024 HH Inc $75K-99999/HHr 45-54" },
      { id: "incomebyage.A45I100_CY", label: "2024 HH Inc 100K-149999/HHr 45-54" },
      { id: "incomebyage.A45I150_CY", label: "2024 HH Inc 150K-199999/HHr 45-54" },
      { id: "incomebyage.A45I200_CY", label: "2024 HH Inc $200000+/HHr 45-54" },
      { id: "incomebyage.IA45BASECY", label: "2024 HH Income Base: HHr 45-54" },
      // Other Age Group Income Bases
      { id: "incomebyage.IA55BASECY", label: "2024 HH Income Base: HHr 55-64" },
      { id: "incomebyage.IA65BASECY", label: "2024 HH Income Base: HHr 65-74" },
      { id: "incomebyage.IA75BASECY", label: "2024 HH Income Base: HHr 75+" },

      // Current Year Net Worth
      { id: "networth.NW0_CY", label: "2024 Net Worth <$15000" },
      { id: "networth.NW15_CY", label: "2024 Net Worth $15000-$34999" },
      { id: "networth.NW35_CY", label: "2024 Net Worth $35000-$49999" },
      { id: "networth.NW50_CY", label: "2024 Net Worth $50000-$74999" },
      { id: "networth.NW75_CY", label: "2024 Net Worth $75000-$99999" },
      { id: "networth.NW100_CY", label: "2024 Net Worth $100000-$149999" },
      { id: "networth.NW150_CY", label: "2024 Net Worth $150000-$249999" },
      { id: "networth.NW250_CY", label: "2024 Net Worth $250000-$499999" },
      { id: "networth.NW500_CY", label: "2024 Net Worth $500000-$999999" },
      { id: "networth.NW1M_CY", label: "2024 Net Worth $1000000-$1499999" },
      { id: "networth.NW1PT5M_CY", label: "2024 Net Worth $1500000-$1999999" },
      { id: "networth.NW2M_CY", label: "2024 Net Worth $2000000+" },
      { id: "networth.MEDNW_CY", label: "2024 Median Net Worth" },
      { id: "networth.AVGNW_CY", label: "2024 Average Net Worth" },

      // Current Year Housing
      { id: "KeyUSFacts.TOTHU_CY", label: "2024 Total Housing Units" },
      { id: "KeyUSFacts.OWNER_CY", label: "2024 Owner Occupied HUs" },
      { id: "KeyUSFacts.RENTER_CY", label: "2024 Renter Occupied HUs" },
      { id: "KeyUSFacts.VACANT_CY", label: "2024 Vacant Housing Units" },

      // Future Year Population
      { id: "KeyUSFacts.TOTPOP_FY", label: "2029 Total Population" },
      { id: "KeyUSFacts.TOTHH_FY", label: "2029 Total Households" },
      { id: "householdtotals.AVGHHSZ_FY", label: "2029 Average Household Size" },

      // Future Year Population by Age
      { id: "5yearincrements.POP0_FY", label: "2029 Population Age 0-4" },
      { id: "5yearincrements.POP5_FY", label: "2029 Population Age 5-9" },
      { id: "5yearincrements.POP10_FY", label: "2029 Population Age 10-14" },
      { id: "5yearincrements.POP15_FY", label: "2029 Population Age 15-19" },
      { id: "5yearincrements.POP20_FY", label: "2029 Population Age 20-24" },
      { id: "5yearincrements.POP25_FY", label: "2029 Population Age 25-29" },
      { id: "5yearincrements.POP30_FY", label: "2029 Population Age 30-34" },
      { id: "5yearincrements.POP35_FY", label: "2029 Population Age 35-39" },
      { id: "5yearincrements.POP40_FY", label: "2029 Population Age 40-44" },
      { id: "5yearincrements.POP45_FY", label: "2029 Population Age 45-49" },
      { id: "5yearincrements.POP50_FY", label: "2029 Population Age 50-54" },
      { id: "5yearincrements.POP55_FY", label: "2029 Population Age 55-59" },
      { id: "5yearincrements.POP60_FY", label: "2029 Population Age 60-64" },
      { id: "5yearincrements.POP65_FY", label: "2029 Population Age 65-69" },
      { id: "5yearincrements.POP70_FY", label: "2029 Population Age 70-74" },
      { id: "5yearincrements.POP75_FY", label: "2029 Population Age 75-79" },
      { id: "5yearincrements.POP80_FY", label: "2029 Population Age 80-84" },
      { id: "5yearincrements.POP85_FY", label: "2029 Population Age 85+" },
      { id: "5yearincrements.MEDAGE_FY", label: "2029 Median Age" },

      // Future Year Household Income
      { id: "householdincome.HINC0_FY", label: "2029 HH Income <$15000" },
      { id: "householdincome.HINC15_FY", label: "2029 HH Income $15000-24999" },
      { id: "householdincome.HINC25_FY", label: "2029 HH Income $25000-34999" },
      { id: "householdincome.HINC35_FY", label: "2029 HH Income $35000-49999" },
      { id: "householdincome.HINC50_FY", label: "2029 HH Income $50000-74999" },
      { id: "householdincome.HINC75_FY", label: "2029 HH Income $75000-99999" },
      { id: "householdincome.HINC100_FY", label: "2029 HH Income $100000-149999" },
      { id: "householdincome.HINC150_FY", label: "2029 HH Income $150000-199999" },
      { id: "householdincome.HINC200_FY", label: "2029 HH Income $200000+" },
      { id: "householdincome.MEDHINC_FY", label: "2029 Median Household Income" },
      { id: "householdincome.AVGHINC_FY", label: "2029 Average Household Income" },

      // Future Year Housing
      { id: "KeyUSFacts.TOTHU_FY", label: "2029 Total Housing Units" },
      { id: "KeyUSFacts.OWNER_FY", label: "2029 Owner Occupied HUs" },
      { id: "KeyUSFacts.RENTER_FY", label: "2029 Renter Occupied HUs" },
      { id: "KeyUSFacts.VACANT_FY", label: "2029 Vacant Housing Units" },

      { id: "HistoricalPopulation.TSPOP20_CY", label: "2020 Total Population" },
      { id: "HistoricalHouseholds.TSHH20_CY", label: "2020 Total   Households" },

      // Historical (2010) Population
      { id: "HistoricalPopulation.TSPOP10_CY", label: "2010 Total Population" },
      { id: "HistoricalHouseholds.TSHH10_CY", label: "2010 Total Households" },
      { id: "5yearincrements.POP0C10", label: "2010 Population Age 0-4" },
      { id: "5yearincrements.POP5C10", label: "2010 Population Age 5-9" },
      { id: "5yearincrements.POP10C10", label: "2010 Population Age 10-14" },
      { id: "5yearincrements.POP15C10", label: "2010 Population Age 15-19" },
      { id: "5yearincrements.POP20C10", label: "2010 Population Age 20-24" },
      { id: "5yearincrements.POP25C10", label: "2010 Population Age 25-29" },
      { id: "5yearincrements.POP30C10", label: "2010 Population Age 30-34" },
      { id: "5yearincrements.POP35C10", label: "2010 Population Age 35-39" },
      { id: "5yearincrements.POP40C10", label: "2010 Population Age 40-44" },
      { id: "5yearincrements.POP45C10", label: "2010 Population Age 45-49" },
      { id: "5yearincrements.POP50C10", label: "2010 Population Age 50-54" },
      { id: "5yearincrements.POP55C10", label: "2010 Population Age 55-59" },
      { id: "5yearincrements.POP60C10", label: "2010 Population Age 60-64" },
      { id: "5yearincrements.POP65C10", label: "2010 Population Age 65-69" },
      { id: "5yearincrements.POP70C10", label: "2010 Population Age 70-74" },
      { id: "5yearincrements.POP75C10", label: "2010 Population Age 75-79" },
      { id: "5yearincrements.POP80C10", label: "2010 Population Age 80-84" },
      { id: "5yearincrements.POP85C10", label: "2010 Population Age 85+" },
      { id: "5yearincrements.MEDAGE10", label: "2010 Median Age" },

      // Historical (2010) Households
      { id: "householdsbysize.FAM2PERS10", label: "2010 Family HHs: 2-Person" },
      { id: "householdsbysize.NF1PERS10", label: "2010 Nonfamily HHs: 1-Person" },
      { id: "householdsbysize.NF2PERS10", label: "2010 Nonfamily HHs: 2-Person" },
      // Tapestry Segments
      { id: "tapestryhouseholdsNEW.THH01", label: "2024 HHs in Tapestry Seg 1A" },
      { id: "tapestryhouseholdsNEW.THH02", label: "2024 HHs in Tapestry Seg 1B" },
      { id: "tapestryhouseholdsNEW.THH03", label: "2024 HHs in Tapestry Seg 1C" },
      { id: "tapestryhouseholdsNEW.THH04", label: "2024 HHs in Tapestry Seg 1D" },
      { id: "tapestryhouseholdsNEW.THH05", label: "2024 HHs in Tapestry Seg 1E" },
      { id: "tapestryhouseholdsNEW.THH06", label: "2024 HHs in Tapestry Seg 2A" },
      { id: "tapestryhouseholdsNEW.THH07", label: "2024 HHs in Tapestry Seg 2B" },
      { id: "tapestryhouseholdsNEW.THH08", label: "2024 HHs in Tapestry Seg 2C" },
      { id: "tapestryhouseholdsNEW.THH09", label: "2024 HHs in Tapestry Seg 2D" },
      { id: "tapestryhouseholdsNEW.THH10", label: "2024 HHs in Tapestry Seg 3A" },
      { id: "tapestryhouseholdsNEW.THH11", label: "2024 HHs in Tapestry Seg 3B" },
      { id: "tapestryhouseholdsNEW.THH12", label: "2024 HHs in Tapestry Seg 3C" },
      { id: "tapestryhouseholdsNEW.THH13", label: "2024 HHs in Tapestry Seg 4A" },
      { id: "tapestryhouseholdsNEW.THH14", label: "2024 HHs in Tapestry Seg 4B" },
      { id: "tapestryhouseholdsNEW.THH15", label: "2024 HHs in Tapestry Seg 4C" },
      { id: "tapestryhouseholdsNEW.THH16", label: "2024 HHs in Tapestry Seg 5A" },
      { id: "tapestryhouseholdsNEW.THH17", label: "2024 HHs in Tapestry Seg 5B" },
      { id: "tapestryhouseholdsNEW.THH18", label: "2024 HHs in Tapestry Seg 5C" },
      { id: "tapestryhouseholdsNEW.THH19", label: "2024 HHs in Tapestry Seg 5D" },
      { id: "tapestryhouseholdsNEW.THH20", label: "2024 HHs in Tapestry Seg 5E" },
      { id: "tapestryhouseholdsNEW.THH21", label: "2024 HHs in Tapestry Seg 6A" },
      { id: "tapestryhouseholdsNEW.THH22", label: "2024 HHs in Tapestry Seg 6B" },
      { id: "tapestryhouseholdsNEW.THH23", label: "2024 HHs in Tapestry Seg 6C" },
      { id: "tapestryhouseholdsNEW.THH24", label: "2024 HHs in Tapestry Seg 6D" },
      { id: "tapestryhouseholdsNEW.THH25", label: "2024 HHs in Tapestry Seg 6E" },
      { id: "tapestryhouseholdsNEW.THH26", label: "2024 HHs in Tapestry Seg 6F" },
      { id: "tapestryhouseholdsNEW.THH27", label: "2024 HHs in Tapestry Seg 7A" },
      { id: "tapestryhouseholdsNEW.THH28", label: "2024 HHs in Tapestry Seg 7B" },
      { id: "tapestryhouseholdsNEW.THH29", label: "2024 HHs in Tapestry Seg 7C" },
      { id: "tapestryhouseholdsNEW.THH30", label: "2024 HHs in Tapestry Seg 7D" },
      { id: "tapestryhouseholdsNEW.THH31", label: "2024 HHs in Tapestry Seg 7E" },
      { id: "tapestryhouseholdsNEW.THH32", label: "2024 HHs in Tapestry Seg 7F" },
      { id: "tapestryhouseholdsNEW.THH33", label: "2024 HHs in Tapestry Seg 8A" },
      { id: "tapestryhouseholdsNEW.THH34", label: "2024 HHs in Tapestry Seg 8B" },
      { id: "tapestryhouseholdsNEW.THH35", label: "2024 HHs in Tapestry Seg 8C" },
      { id: "tapestryhouseholdsNEW.THH36", label: "2024 HHs in Tapestry Seg 8D" },
      { id: "tapestryhouseholdsNEW.THH37", label: "2024 HHs in Tapestry Seg 8E" },
      { id: "tapestryhouseholdsNEW.THH38", label: "2024 HHs in Tapestry Seg 8F" },
      { id: "tapestryhouseholdsNEW.THH39", label: "2024 HHs in Tapestry Seg 8G" },
      { id: "tapestryhouseholdsNEW.THH40", label: "2024 HHs in Tapestry Seg 9A" },
      { id: "tapestryhouseholdsNEW.THH41", label: "2024 HHs in Tapestry Seg 9B" },
      { id: "tapestryhouseholdsNEW.THH42", label: "2024 HHs in Tapestry Seg 9C" },
      { id: "tapestryhouseholdsNEW.THH43", label: "2024 HHs in Tapestry Seg 9D" },
      { id: "tapestryhouseholdsNEW.THH44", label: "2024 HHs in Tapestry Seg 9E" },
      { id: "tapestryhouseholdsNEW.THH45", label: "2024 HHs in Tapestry Seg 9F" },
      { id: "tapestryhouseholdsNEW.THH46", label: "2024 HHs in Tapestry Seg 10A" },
      { id: "tapestryhouseholdsNEW.THH47", label: "2024 HHs in Tapestry Seg 10B" },
      { id: "tapestryhouseholdsNEW.THH48", label: "2024 HHs in Tapestry Seg 10C" },
      { id: "tapestryhouseholdsNEW.THH49", label: "2024 HHs in Tapestry Seg 10D" },
      { id: "tapestryhouseholdsNEW.THH50", label: "2024 HHs in Tapestry Seg 10E" },
      { id: "tapestryhouseholdsNEW.THH51", label: "2024 HHs in Tapestry Seg 11A" },
      { id: "tapestryhouseholdsNEW.THH52", label: "2024 HHs in Tapestry Seg 11B" },
      { id: "tapestryhouseholdsNEW.THH53", label: "2024 HHs in Tapestry Seg 11C" },
      { id: "tapestryhouseholdsNEW.THH54", label: "2024 HHs in Tapestry Seg 11D" },
      { id: "tapestryhouseholdsNEW.THH55", label: "2024 HHs in Tapestry Seg 11E" },
      { id: "tapestryhouseholdsNEW.THH56", label: "2024 HHs in Tapestry Seg 12A" },
      { id: "tapestryhouseholdsNEW.THH57", label: "2024 HHs in Tapestry Seg 12B" },
      { id: "tapestryhouseholdsNEW.THH58", label: "2024 HHs in Tapestry Seg 12C" },
      { id: "tapestryhouseholdsNEW.THH59", label: "2024 HHs in Tapestry Seg 12D" },
      { id: "tapestryhouseholdsNEW.THH60", label: "2024 HHs in Tapestry Seg 13A" },
      { id: "tapestryhouseholdsNEW.THH61", label: "2024 HHs in Tapestry Seg 13B" },
      { id: "tapestryhouseholdsNEW.THH62", label: "2024 HHs in Tapestry Seg 13C" },
      { id: "tapestryhouseholdsNEW.THH63", label: "2024 HHs in Tapestry Seg 13D" },
      { id: "tapestryhouseholdsNEW.THH64", label: "2024 HHs in Tapestry Seg 13E" },
      { id: "tapestryhouseholdsNEW.THH65", label: "2024 HHs in Tapestry Seg 14A" },
      { id: "tapestryhouseholdsNEW.THH66", label: "2024 HHs in Tapestry Seg 14B" },
      { id: "tapestryhouseholdsNEW.THH67", label: "2024 HHs in Tapestry Seg 14C" },
    ]
  },
  tier2: {
    label: "Tier 2 Variables",
    variables: [
      // Current Year 1-Year Age Increments
      { id: "1yearincrements.AGE0_CY", label: "2024 Population Age <1" },
      { id: "1yearincrements.AGE1_CY", label: "2024 Population Age 1" },
      { id: "1yearincrements.AGE2_CY", label: "2024 Population Age 2" },
      { id: "1yearincrements.AGE3_CY", label: "2024 Population Age 3" },
      { id: "1yearincrements.AGE4_CY", label: "2024 Population Age 4" },
      { id: "1yearincrements.AGE5_CY", label: "2024 Population Age 5" },
      { id: "1yearincrements.AGE6_CY", label: "2024 Population Age 6" },
      { id: "1yearincrements.AGE7_CY", label: "2024 Population Age 7" },
      { id: "1yearincrements.AGE8_CY", label: "2024 Population Age 8" },
      { id: "1yearincrements.AGE9_CY", label: "2024 Population Age 9" },
      { id: "1yearincrements.AGE10_CY", label: "2024 Population Age 10" },
      { id: "1yearincrements.AGE11_CY", label: "2024 Population Age 11" },
      { id: "1yearincrements.AGE12_CY", label: "2024 Population Age 12" },
      { id: "1yearincrements.AGE13_CY", label: "2024 Population Age 13" },
      { id: "1yearincrements.AGE14_CY", label: "2024 Population Age 14" },
      { id: "1yearincrements.AGE15_CY", label: "2024 Population Age 15" },
      { id: "1yearincrements.AGE16_CY", label: "2024 Population Age 16" },
      { id: "1yearincrements.AGE17_CY", label: "2024 Population Age 17" },
      { id: "1yearincrements.AGE18_CY", label: "2024 Population Age 18" },
      { id: "1yearincrements.AGE19_CY", label: "2024 Population Age 19" },
      { id: "1yearincrements.AGE20_CY", label: "2024 Population Age 20" },
      { id: "1yearincrements.AGE21_CY", label: "2024 Population Age 21" },
      { id: "1yearincrements.AGE22_CY", label: "2024 Population Age 22" },
      { id: "1yearincrements.AGE23_CY", label: "2024 Population Age 23" },
      { id: "1yearincrements.AGE24_CY", label: "2024 Population Age 24" },
      { id: "1yearincrements.AGE25_CY", label: "2024 Population Age 25" },
      { id: "1yearincrements.AGE26_CY", label: "2024 Population Age 26" },
      { id: "1yearincrements.AGE27_CY", label: "2024 Population Age 27" },
      { id: "1yearincrements.AGE28_CY", label: "2024 Population Age 28" },
      { id: "1yearincrements.AGE29_CY", label: "2024 Population Age 29" },
      { id: "1yearincrements.AGE30_CY", label: "2024 Population Age 30" },
      { id: "1yearincrements.AGE31_CY", label: "2024 Population Age 31" },
      { id: "1yearincrements.AGE32_CY", label: "2024 Population Age 32" },
      { id: "1yearincrements.AGE33_CY", label: "2024 Population Age 33" },
      { id: "1yearincrements.AGE34_CY", label: "2024 Population Age 34" },
      { id: "1yearincrements.AGE35_CY", label: "2024 Population Age 35" },
      { id: "1yearincrements.AGE36_CY", label: "2024 Population Age 36" },
      { id: "1yearincrements.AGE37_CY", label: "2024 Population Age 37" },
      { id: "1yearincrements.AGE38_CY", label: "2024 Population Age 38" },
      { id: "1yearincrements.AGE39_CY", label: "2024 Population Age 39" },
      { id: "1yearincrements.AGE40_CY", label: "2024 Population Age 40" },
      { id: "1yearincrements.AGE41_CY", label: "2024 Population Age 41" },
      { id: "1yearincrements.AGE42_CY", label: "2024 Population Age 42" },
      { id: "1yearincrements.AGE43_CY", label: "2024 Population Age 43" },
      { id: "1yearincrements.AGE44_CY", label: "2024 Population Age 44" },
      { id: "1yearincrements.AGE45_CY", label: "2024 Population Age 45" },
      { id: "1yearincrements.AGE46_CY", label: "2024 Population Age 46" },
      { id: "1yearincrements.AGE47_CY", label: "2024 Population Age 47" },
      { id: "1yearincrements.AGE48_CY", label: "2024 Population Age 48" },
      { id: "1yearincrements.AGE49_CY", label: "2024 Population Age 49" },
      { id: "1yearincrements.AGE50_CY", label: "2024 Population Age 50" },
      { id: "1yearincrements.AGE51_CY", label: "2024 Population Age 51" },
      { id: "1yearincrements.AGE52_CY", label: "2024 Population Age 52" },
      { id: "1yearincrements.AGE53_CY", label: "2024 Population Age 53" },
      { id: "1yearincrements.AGE54_CY", label: "2024 Population Age 54" },
      { id: "1yearincrements.AGE55_CY", label: "2024 Population Age 55" },
      { id: "1yearincrements.AGE56_CY", label: "2024 Population Age 56" },
      { id: "1yearincrements.AGE57_CY", label: "2024 Population Age 57" },
      { id: "1yearincrements.AGE58_CY", label: "2024 Population Age 58" },
      { id: "1yearincrements.AGE59_CY", label: "2024 Population Age 59" },
      { id: "1yearincrements.AGE60_CY", label: "2024 Population Age 60" },
      { id: "1yearincrements.AGE61_CY", label: "2024 Population Age 61" },
      { id: "1yearincrements.AGE62_CY", label: "2024 Population Age 62" },
      { id: "1yearincrements.AGE63_CY", label: "2024 Population Age 63" },
      { id: "1yearincrements.AGE64_CY", label: "2024 Population Age 64" },
      { id: "1yearincrements.AGE65_CY", label: "2024 Population Age 65" },
      { id: "1yearincrements.AGE66_CY", label: "2024 Population Age 66" },
      { id: "1yearincrements.AGE67_CY", label: "2024 Population Age 67" },
      { id: "1yearincrements.AGE68_CY", label: "2024 Population Age 68" },
      { id: "1yearincrements.AGE69_CY", label: "2024 Population Age 69" },
      { id: "1yearincrements.AGE70_CY", label: "2024 Population Age 70" },
      { id: "1yearincrements.AGE71_CY", label: "2024 Population Age 71" },
      { id: "1yearincrements.AGE72_CY", label: "2024 Population Age 72" },
      { id: "1yearincrements.AGE73_CY", label: "2024 Population Age 73" },
      { id: "1yearincrements.AGE74_CY", label: "2024 Population Age 74" },
      { id: "1yearincrements.AGE75_CY", label: "2024 Population Age 75" },
      { id: "1yearincrements.AGE76_CY", label: "2024 Population Age 76" },
      { id: "1yearincrements.AGE77_CY", label: "2024 Population Age 77" },
      { id: "1yearincrements.AGE78_CY", label: "2024 Population Age 78" },
      { id: "1yearincrements.AGE79_CY", label: "2024 Population Age 79" },
      { id: "1yearincrements.AGE80_CY", label: "2024 Population Age 80" },
      { id: "1yearincrements.AGE81_CY", label: "2024 Population Age 81" },
      { id: "1yearincrements.AGE82_CY", label: "2024 Population Age 82" },
      { id: "1yearincrements.AGE83_CY", label: "2024 Population Age 83" },
      { id: "1yearincrements.AGE84_CY", label: "2024 Population Age 84" },

      // Future Year 1-Year Age Increments
      { id: "1yearincrements.AGE0_FY", label: "2029 Population Age <1" },
      { id: "1yearincrements.AGE1_FY", label: "2029 Population Age 1" },
      { id: "1yearincrements.AGE2_FY", label: "2029 Population Age 2" },
      { id: "1yearincrements.AGE3_FY", label: "2029 Population Age 3" },
      { id: "1yearincrements.AGE4_FY", label: "2029 Population Age 4" },
      { id: "1yearincrements.AGE5_FY", label: "2029 Population Age 5" },
      { id: "1yearincrements.AGE6_FY", label: "2029 Population Age 6" },
      { id: "1yearincrements.AGE7_FY", label: "2029 Population Age 7" },
      { id: "1yearincrements.AGE8_FY", label: "2029 Population Age 8" },
      { id: "1yearincrements.AGE9_FY", label: "2029 Population Age 9" },
      { id: "1yearincrements.AGE10_FY", label: "2029 Population Age 10" },
      { id: "1yearincrements.AGE11_FY", label: "2029 Population Age 11" },
      { id: "1yearincrements.AGE12_FY", label: "2029 Population Age 12" },
      { id: "1yearincrements.AGE13_FY", label: "2029 Population Age 13" },
      { id: "1yearincrements.AGE14_FY", label: "2029 Population Age 14" },
      { id: "1yearincrements.AGE15_FY", label: "2029 Population Age 15" },

      { id: "1yearincrements.AGE16_FY", label: "2029 Population Age 16" },
      { id: "1yearincrements.AGE17_FY", label: "2029 Population Age 17" },
      { id: "1yearincrements.AGE18_FY", label: "2029 Population Age 18" },
      { id: "1yearincrements.AGE19_FY", label: "2029 Population Age 19" },
      { id: "1yearincrements.AGE20_FY", label: "2029 Population Age 20" },
      { id: "1yearincrements.AGE21_FY", label: "2029 Population Age 21" },
      { id: "1yearincrements.AGE22_FY", label: "2029 Population Age 22" },
      { id: "1yearincrements.AGE23_FY", label: "2029 Population Age 23" },
      { id: "1yearincrements.AGE24_FY", label: "2029 Population Age 24" },
      { id: "1yearincrements.AGE25_FY", label: "2029 Population Age 25" },
      { id: "1yearincrements.AGE26_FY", label: "2029 Population Age 26" },
      { id: "1yearincrements.AGE27_FY", label: "2029 Population Age 27" },
      { id: "1yearincrements.AGE28_FY", label: "2029 Population Age 28" },
      { id: "1yearincrements.AGE29_FY", label: "2029 Population Age 29" },
      { id: "1yearincrements.AGE30_FY", label: "2029 Population Age 30" },
      { id: "1yearincrements.AGE31_FY", label: "2029 Population Age 31" },
      { id: "1yearincrements.AGE32_FY", label: "2029 Population Age 32" },
      { id: "1yearincrements.AGE33_FY", label: "2029 Population Age 33" },
      { id: "1yearincrements.AGE34_FY", label: "2029 Population Age 34" },
      { id: "1yearincrements.AGE35_FY", label: "2029 Population Age 35" },
      { id: "1yearincrements.AGE36_FY", label: "2029 Population Age 36" },
      { id: "1yearincrements.AGE37_FY", label: "2029 Population Age 37" },
      { id: "1yearincrements.AGE38_FY", label: "2029 Population Age 38" },
      { id: "1yearincrements.AGE39_FY", label: "2029 Population Age 39" },
      { id: "1yearincrements.AGE40_FY", label: "2029 Population Age 40" },
      { id: "1yearincrements.AGE41_FY", label: "2029 Population Age 41" },
      { id: "1yearincrements.AGE42_FY", label: "2029 Population Age 42" },
      { id: "1yearincrements.AGE43_FY", label: "2029 Population Age 43" },
      { id: "1yearincrements.AGE44_FY", label: "2029 Population Age 44" },
      { id: "1yearincrements.AGE45_FY", label: "2029 Population Age 45" },
      { id: "1yearincrements.AGE46_FY", label: "2029 Population Age 46" },
      { id: "1yearincrements.AGE47_FY", label: "2029 Population Age 47" },
      { id: "1yearincrements.AGE48_FY", label: "2029 Population Age 48" },
      { id: "1yearincrements.AGE49_FY", label: "2029 Population Age 49" },
      { id: "1yearincrements.AGE50_FY", label: "2029 Population Age 50" },
      { id: "1yearincrements.AGE51_FY", label: "2029 Population Age 51" },
      { id: "1yearincrements.AGE52_FY", label: "2029 Population Age 52" },
      { id: "1yearincrements.AGE53_FY", label: "2029 Population Age 53" },
      { id: "1yearincrements.AGE54_FY", label: "2029 Population Age 54" },
      { id: "1yearincrements.AGE55_FY", label: "2029 Population Age 55" },
      { id: "1yearincrements.AGE56_FY", label: "2029 Population Age 56" },
      { id: "1yearincrements.AGE57_FY", label: "2029 Population Age 57" },
      { id: "1yearincrements.AGE58_FY", label: "2029 Population Age 58" },
      { id: "1yearincrements.AGE59_FY", label: "2029 Population Age 59" },
      { id: "1yearincrements.AGE60_FY", label: "2029 Population Age 60" },
      { id: "1yearincrements.AGE61_FY", label: "2029 Population Age 61" },
      { id: "1yearincrements.AGE62_FY", label: "2029 Population Age 62" },
      { id: "1yearincrements.AGE63_FY", label: "2029 Population Age 63" },
      { id: "1yearincrements.AGE64_FY", label: "2029 Population Age 64" },
      { id: "1yearincrements.AGE65_FY", label: "2029 Population Age 65" },
      { id: "1yearincrements.AGE66_FY", label: "2029 Population Age 66" },
      { id: "1yearincrements.AGE67_FY", label: "2029 Population Age 67" },
      { id: "1yearincrements.AGE68_FY", label: "2029 Population Age 68" },
      { id: "1yearincrements.AGE69_FY", label: "2029 Population Age 69" },
      { id: "1yearincrements.AGE70_FY", label: "2029 Population Age 70" },
      { id: "1yearincrements.AGE71_FY", label: "2029 Population Age 71" },
      { id: "1yearincrements.AGE72_FY", label: "2029 Population Age 72" },
      { id: "1yearincrements.AGE73_FY", label: "2029 Population Age 73" },
      { id: "1yearincrements.AGE74_FY", label: "2029 Population Age 74" },
      { id: "1yearincrements.AGE75_FY", label: "2029 Population Age 75" },
      { id: "1yearincrements.AGE76_FY", label: "2029 Population Age 76" },
      { id: "1yearincrements.AGE77_FY", label: "2029 Population Age 77" },
      { id: "1yearincrements.AGE78_FY", label: "2029 Population Age 78" },
      { id: "1yearincrements.AGE79_FY", label: "2029 Population Age 79" },
      { id: "1yearincrements.AGE80_FY", label: "2029 Population Age 80" },
      { id: "1yearincrements.AGE81_FY", label: "2029 Population Age 81" },
      { id: "1yearincrements.AGE82_FY", label: "2029 Population Age 82" },
      { id: "1yearincrements.AGE83_FY", label: "2029 Population Age 83" },
      { id: "1yearincrements.AGE84_FY", label: "2029 Population Age 84" },

      // Educational Attainment
      { id: "educationalattainment.HSGRAD_CY", label: "2024 Pop Age 25+: High School Diploma" },
      { id: "educationalattainment.GED_CY", label: "2024 Pop Age 25+: GED" },
      { id: "educationalattainment.SMCOLL_CY", label: "2024 Pop Age 25+: Some College/No Degree" },
      { id: "educationalattainment.ASSCDEG_CY", label: "2024 Pop Age 25+: Associate's Degree" },
      { id: "educationalattainment.BACHDEG_CY", label: "2024 Pop Age 25+: Bachelor's Degree" },
      { id: "educationalattainment.GRADDEG_CY", label: "2024 Pop Age 25+: Grad/Professional Degree" },
      { id: "educationalattainment.EDUCBASECY", label: "2024 Educational Attainment Base" },

      // Future Year Income Base by Age
      { id: "incomebyage.IA15BASEFY", label: "2029 HH Income Base: HHr 15-24" },
      { id: "incomebyage.IA25BASEFY", label: "2029 HH Income Base: HHr 25-34" },
      { id: "incomebyage.IA35BASEFY", label: "2029 HH Income Base: HHr 35-44" },
      { id: "incomebyage.IA45BASEFY", label: "2029 HH Income Base: HHr 45-54" },
      { id: "incomebyage.IA55BASEFY", label: "2029 HH Income Base: HHr 55-64" },
      { id: "incomebyage.IA65BASEFY", label: "2029 HH Income Base: HHr 65-74" },
      { id: "incomebyage.IA75BASEFY", label: "2029 HH Income Base: HHr 75+" },

      { id: "incomebyage.MEDIA15_CY", label: "2024 Median HH Inc: HHr 15-24" },
      { id: "incomebyage.MEDIA25_CY", label: "2024 Median HH Inc: HHr 25-34" },
      { id: "incomebyage.MEDIA35_CY", label: "2024 Median HH Inc: HHr 35-44" },
      { id: "incomebyage.MEDIA45_CY", label: "2024 Median HH Inc: HHr 45-54" },
      { id: "incomebyage.MEDIA55_CY", label: "2024 Median HH Inc: HHr 55-64" },
      { id: "incomebyage.MEDIA65_CY", label: "2024 Median HH Inc: HHr 65-74" },
      { id: "incomebyage.MEDIA75_CY", label: "2024 Median HH Inc: HHr 75+" },


      { id: "incomebyage.A15I0_CY", label: "2024 HH Inc <$15000/HHr 15-24" },
      { id: "incomebyage.A15I15_CY", label: "2024 HH Inc $15K-24999/HHr 15-24" },
      { id: "incomebyage.A15I25_CY", label: "2024 HH Inc $25K-34999/HHr 15-24" },
      { id: "incomebyage.A15I35_CY", label: "2024 HH Inc $35K-49999/HHr 15-24" },
      { id: "incomebyage.A15I50_CY", label: "2024 HH Inc $50K-74999/HHr 15-24" },
      { id: "incomebyage.A15I75_CY", label: "2024 HH Inc $75K-99999/HHr 15-24" },
      { id: "incomebyage.A15I100_CY", label: "2024 HH Inc 100K-149999/HHr 15-24" },
      { id: "incomebyage.A15I150_CY", label: "2024 HH Inc 150K-199999/HHr 15-24" },
      { id: "incomebyage.A15I200_CY", label: "2024 HH Inc $200000+/HHr 15-24" },
      { id: "incomebyage.A55I0_CY", label: "2024 HH Inc <$15000/HHr 55-64" },
      { id: "incomebyage.A55I15_CY", label: "2024 HH Inc $15K-24999/HHr 55-64" },
      { id: "incomebyage.A55I25_CY", label: "2024 HH Inc $25K-34999/HHr 55-64" },
      { id: "incomebyage.A55I35_CY", label: "2024 HH Inc $35K-49999/HHr 55-64" },
      { id: "incomebyage.A55I50_CY", label: "2024 HH Inc $50K-74999/HHr 55-64" },
      { id: "incomebyage.A55I75_CY", label: "2024 HH Inc $75K-99999/HHr 55-64" },
      { id: "incomebyage.A55I100_CY", label: "2024 HH Inc 100K-149999/HHr 55-64" },
      { id: "incomebyage.A55I150_CY", label: "2024 HH Inc 150K-199999/HHr 55-64" },
      { id: "incomebyage.A55I200_CY", label: "2024 HH Inc $200000+/HHr 55-64" },
      { id: "incomebyage.A65I0_CY", label: "2024 HH Inc <$15000/HHr 65-74" },
      { id: "incomebyage.A65I15_CY", label: "2024 HH Inc $15K-24999/HHr 65-74" },
      { id: "incomebyage.A65I25_CY", label: "2024 HH Inc $25K-34999/HHr 65-74" },
      { id: "incomebyage.A65I35_CY", label: "2024 HH Inc $35K-49999/HHr 65-74" },
      { id: "incomebyage.A65I50_CY", label: "2024 HH Inc $50K-74999/HHr 65-74" },
      { id: "incomebyage.A65I75_CY", label: "2024 HH Inc $75K-99999/HHr 65-74" },
      { id: "incomebyage.A65I100_CY", label: "2024 HH Inc 100K-149999/HHr 65-74" },
      { id: "incomebyage.A65I150_CY", label: "2024 HH Inc 150K-199999/HHr 65-74" },
      { id: "incomebyage.A65I200_CY", label: "2024 HH Inc $200000+/HHr 65-74" },
      { id: "incomebyage.A75I0_CY", label: "2024 HH Inc <$15000/HHr 75+" },
      { id: "incomebyage.A75I15_CY", label: "2024 HH Inc $15K-24999/HHr 75+" },
      { id: "incomebyage.A75I25_CY", label: "2024 HH Inc $25K-34999/HHr 75+" },
      { id: "incomebyage.A75I35_CY", label: "2024 HH Inc $35K-49999/HHr 75+" },
      { id: "incomebyage.A75I50_CY", label: "2024 HH Inc $50K-74999/HHr 75+" },
      { id: "incomebyage.A75I75_CY", label: "2024 HH Inc $75K-99999/HHr 75+" },
      { id: "incomebyage.A75I100_CY", label: "2024 HH Inc 100K-149999/HHr 75+" },
      { id: "incomebyage.A75I150_CY", label: "2024 HH Inc 150K-199999/HHr 75+" },
      { id: "incomebyage.A75I200_CY", label: "2024 HH Inc $200000+/HHr 75+" },
      { id: "networth.MEDNWA15CY", label: "2024 Median Net Worth: HHr 15-24" },
      { id: "networth.MEDNWA25CY", label: "2024 Median Net Worth: HHr 25-34" },
      { id: "networth.MEDNWA35CY", label: "2024 Median Net Worth: HHr 35-44" },
      { id: "networth.MEDNWA45CY", label: "2024 Median Net Worth: HHr 45-54" },
      { id: "networth.MEDNWA55CY", label: "2024 Median Net Worth: HHr 55-64" },
      { id: "networth.MEDNWA65CY", label: "2024 Median Net Worth: HHr 65-74" },
      { id: "networth.MEDNWA75CY", label: "2024 Median Net Worth: HHr 75+" },
      { id: "networth.A15NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 15-24" },
      { id: "networth.A25NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 25-34" },
      { id: "networth.A35NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 35-44" },
      { id: "networth.A45NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 45-54" },
      { id: "networth.A55NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 55-64" },
      { id: "networth.A65NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 65-74" },
      { id: "networth.A75NW1M_CY", label: "2024 HH Net Worth $1000000+/HHr 75+" },

      // Age 15-24 Income Brackets
      { id: "incomebyage.A15I0_FY", label: "2029 HH Inc <$15000/HHr 15-24" },
      { id: "incomebyage.A15I15_FY", label: "2029 HH Inc $15K-24999/HHr 15-24" },
      { id: "incomebyage.A15I25_FY", label: "2029 HH Inc $25K-34999/HHr 15-24" },
      { id: "incomebyage.A15I35_FY", label: "2029 HH Inc $35K-49999/HHr 15-24" },
      { id: "incomebyage.A15I50_FY", label: "2029 HH Inc $50K-74999/HHr 15-24" },
      { id: "incomebyage.A15I75_FY", label: "2029 HH Inc $75K-99999/HHr 15-24" },
      { id: "incomebyage.A15I100_FY", label: "2029 HH Inc 100K-149999/HHr 15-24" },
      { id: "incomebyage.A15I150_FY", label: "2029 HH Inc 150K-199999/HHr 15-24" },
      { id: "incomebyage.A15I200_FY", label: "2029 HH Inc $200000+/HHr 15-24" },

      // Age 25-34 Income Brackets
      { id: "incomebyage.A25I0_FY", label: "2029 HH Inc <$15000/HHr 25-34" },
      { id: "incomebyage.A25I15_FY", label: "2029 HH Inc $15K-24999/HHr 25-34" },
      { id: "incomebyage.A25I25_FY", label: "2029 HH Inc $25K-34999/HHr 25-34" },
      { id: "incomebyage.A25I35_FY", label: "2029 HH Inc $35K-49999/HHr 25-34" },
      { id: "incomebyage.A25I50_FY", label: "2029 HH Inc $50K-74999/HHr 25-34" },
      { id: "incomebyage.A25I75_FY", label: "2029 HH Inc $75K-99999/HHr 25-34" },
      { id: "incomebyage.A25I100_FY", label: "2029 HH Inc 100K-149999/HHr 25-34" },
      { id: "incomebyage.A25I150_FY", label: "2029 HH Inc 150K-199999/HHr 25-34" },
      { id: "incomebyage.A25I200_FY", label: "2029 HH Inc $200000+/HHr 25-34" },

      // Age 35-44 Income Brackets
      { id: "incomebyage.A35I0_FY", label: "2029 HH Inc <$15000/HHr 35-44" },
      { id: "incomebyage.A35I15_FY", label: "2029 HH Inc $15K-24999/HHr 35-44" },
      { id: "incomebyage.A35I25_FY", label: "2029 HH Inc $25K-34999/HHr 35-44" },
      { id: "incomebyage.A35I35_FY", label: "2029 HH Inc $35K-49999/HHr 35-44" },
      { id: "incomebyage.A35I50_FY", label: "2029 HH Inc $50K-74999/HHr 35-44" },
      { id: "incomebyage.A35I75_FY", label: "2029 HH Inc $75K-99999/HHr 35-44" },
      { id: "incomebyage.A35I100_FY", label: "2029 HH Inc 100K-149999/HHr 35-44" },
      { id: "incomebyage.A35I150_FY", label: "2029 HH Inc 150K-199999/HHr 35-44" },
      { id: "incomebyage.A35I200_FY", label: "2029 HH Inc $200000+/HHr 35-44" },

      // Age 45-54 Income Brackets
      { id: "incomebyage.A45I0_FY", label: "2029 HH Inc <$15000/HHr 45-54" },
      { id: "incomebyage.A45I15_FY", label: "2029 HH Inc $15K-24999/HHr 45-54" },
      { id: "incomebyage.A45I25_FY", label: "2029 HH Inc $25K-34999/HHr 45-54" },
      { id: "incomebyage.A45I35_FY", label: "2029 HH Inc $35K-49999/HHr 45-54" },
      { id: "incomebyage.A45I50_FY", label: "2029 HH Inc $50K-74999/HHr 45-54" },
      { id: "incomebyage.A45I75_FY", label: "2029 HH Inc $75K-99999/HHr 45-54" },
      { id: "incomebyage.A45I100_FY", label: "2029 HH Inc 100K-149999/HHr 45-54" },
      { id: "incomebyage.A45I150_FY", label: "2029 HH Inc 150K-199999/HHr 45-54" },
      { id: "incomebyage.A45I200_FY", label: "2029 HH Inc $200000+/HHr 45-54" },

      // Age 55-64 Income Brackets
      { id: "incomebyage.A55I0_FY", label: "2029 HH Inc <$15000/HHr 55-64" },
      { id: "incomebyage.A55I15_FY", label: "2029 HH Inc $15K-24999/HHr 55-64" },
      { id: "incomebyage.A55I25_FY", label: "2029 HH Inc $25K-34999/HHr 55-64" },
      { id: "incomebyage.A55I35_FY", label: "2029 HH Inc $35K-49999/HHr 55-64" },
      { id: "incomebyage.A55I50_FY", label: "2029 HH Inc $50K-74999/HHr 55-64" },
      { id: "incomebyage.A55I75_FY", label: "2029 HH Inc $75K-99999/HHr 55-64" },
      { id: "incomebyage.A55I100_FY", label: "2029 HH Inc 100K-149999/HHr 55-64" },
      { id: "incomebyage.A55I150_FY", label: "2029 HH Inc 150K-199999/HHr 55-64" },
      { id: "incomebyage.A55I200_FY", label: "2029 HH Inc $200000+/HHr 55-64" },

      // Age 65-74 Income Brackets
      { id: "incomebyage.A65I0_FY", label: "2029 HH Inc <$15000/HHr 65-74" },
      { id: "incomebyage.A65I15_FY", label: "2029 HH Inc $15K-24999/HHr 65-74" },
      { id: "incomebyage.A65I25_FY", label: "2029 HH Inc $25K-34999/HHr 65-74" },
      { id: "incomebyage.A65I35_FY", label: "2029 HH Inc $35K-49999/HHr 65-74" },
      { id: "incomebyage.A65I50_FY", label: "2029 HH Inc $50K-74999/HHr 65-74" },
      { id: "incomebyage.A65I75_FY", label: "2029 HH Inc $75K-99999/HHr 65-74" },
      { id: "incomebyage.A65I100_FY", label: "2029 HH Inc 100K-149999/HHr 65-74" },
      { id: "incomebyage.A65I150_FY", label: "2029 HH Inc 150K-199999/HHr 65-74" },
      { id: "incomebyage.A65I200_FY", label: "2029 HH Inc $200000+/HHr 65-74" },

      // Age 75+ Income Brackets
      { id: "incomebyage.A75I0_FY", label: "2029 HH Inc <$15000/HHr 75+" },
      { id: "incomebyage.A75I15_FY", label: "2029 HH Inc $15K-24999/HHr 75+" },
      { id: "incomebyage.A75I25_FY", label: "2029 HH Inc $25K-34999/HHr 75+" },
      { id: "incomebyage.A75I35_FY", label: "2029 HH Inc $35K-49999/HHr 75+" },
      { id: "incomebyage.A75I50_FY", label: "2029 HH Inc $50K-74999/HHr 75+" },
      { id: "incomebyage.A75I75_FY", label: "2029 HH Inc $75K-99999/HHr 75+" },
      { id: "incomebyage.A75I100_FY", label: "2029 HH Inc 100K-149999/HHr 75+" },
      { id: "incomebyage.A75I150_FY", label: "2029 HH Inc 150K-199999/HHr 75+" },
      { id: "incomebyage.A75I200_FY", label: "2029 HH Inc $200000+/HHr 75+" },

      { id: "householdsbysize.FAM2PERS10", label: "2010 Family HHs: 2-Person" },
      { id: "householdsbysize.FAM3PERS10", label: "2010 Family HHs: 3-Person" },
      { id: "householdsbysize.FAM4PERS10", label: "2010 Family HHs: 4-Person" },
      { id: "householdsbysize.FAM5PERS10", label: "2010 Family HHs: 5-Person" },
      { id: "householdsbysize.FAM6PERS10", label: "2010 Family HHs: 6-Person" },
      { id: "householdsbysize.FAM7PERS10", label: "2010 Family HHs: 7+-Person" },
      { id: "householdsbysize.NF1PERS10", label: "2010 Nonfamily HHs: 1-Person" },
      { id: "householdsbysize.NF2PERS10", label: "2010 Nonfamily HHs: 2-Person" },
      { id: "householdsbysize.NF3PERS10", label: "2010 Nonfamily HHs: 3-Person" },
      { id: "householdsbysize.NF4PERS10", label: "2010 Nonfamily HHs: 4-Person" },
      { id: "householdsbysize.NF5PERS10", label: "2010 Nonfamily HHs: 5-Person" },
      { id: "householdsbysize.NF6PERS10", label: "2010 Nonfamily HHs: 6-Person" },
      { id: "householdsbysize.NF7PERS10", label: "2010 Nonfamily HHs: 7+-Person" },

      // Income by Age (Average)
      { id: "incomebyage.AVGIA15_CY", label: "2024 Average HH Inc: HHr 15-24" },
      { id: "incomebyage.AVGIA25_CY", label: "2024 Average HH Inc: HHr 25-34" },
      { id: "incomebyage.AVGIA35_CY", label: "2024 Average HH Inc: HHr 35-44" },
      { id: "incomebyage.AVGIA45_CY", label: "2024 Average HH Inc: HHr 45-54" },
      { id: "incomebyage.AVGIA55_CY", label: "2024 Average HH Inc: HHr 55-64" },
      { id: "incomebyage.AVGIA65_CY", label: "2024 Average HH Inc: HHr 65-74" },
      { id: "incomebyage.AVGIA75_CY", label: "2024 Average HH Inc: HHr 75+" },
    
      { id: "incomebyage.AVGIA15_CY", label: "2024 Average HH Inc: HHr 15-24" },
      { id: "incomebyage.AVGIA25_CY", label: "2024 Average HH Inc: HHr 25-34" },
      { id: "incomebyage.AVGIA35_CY", label: "2024 Average HH Inc: HHr 35-44" },
      { id: "incomebyage.AVGIA45_CY", label: "2024 Average HH Inc: HHr 45-54" },
      { id: "incomebyage.AVGIA55_CY", label: "2024 Average HH Inc: HHr 55-64" },
      { id: "incomebyage.AVGIA65_CY", label: "2024 Average HH Inc: HHr 65-74" },
      { id: "incomebyage.AVGIA75_CY", label: "2024 Average HH Inc: HHr 75+" },

      // NEW VARIABLES - Add these at the end of tier2 variables array
      // Future Year Median Household Income by Age Groups
      { id: "incomebyage.MEDIA15_FY", label: "2029 Median HH Inc: HHr 15-24" },
      { id: "incomebyage.MEDIA25_FY", label: "2029 Median HH Inc: HHr 25-34" },
      { id: "incomebyage.MEDIA35_FY", label: "2029 Median HH Inc: HHr 35-44" },
      { id: "incomebyage.MEDIA45_FY", label: "2029 Median HH Inc: HHr 45-54" },
      { id: "incomebyage.MEDIA55_FY", label: "2029 Median HH Inc: HHr 55-64" },
      { id: "incomebyage.MEDIA65_FY", label: "2029 Median HH Inc: HHr 65-74" },
      { id: "incomebyage.MEDIA75_FY", label: "2029 Median HH Inc: HHr 75+" },

      // Median Household Income by Age Groups - Consolidated Age Ranges
      { id: "incomebyage.MEDIA55UFY", label: "2029 Median HH Inc: HHr 55+" },
      { id: "incomebyage.MEDIA65UFY", label: "2029 Median HH Inc: HHr 65+" },
      { id: "incomebyage.MEDIA65UCY", label: "2024 Median HH Inc: HHr 65+" },
      { id: "incomebyage.MEDIA55UCY", label: "2024 Median HH Inc: HHr 55+" },
      { id: "incomebyage.MEDIA75_CY", label: "2024 Median HH Inc: HHr 75+" },
    ]
  },

  
  retail: {
    label: "Retail Variables",
    variables: [
      { id: "HousingHousehold.X4043_A", label: "2024 Household Furnishings & Equipment (Average)" },
      { id: "entertainment.X9051_A", label: "2024 Sports/Rec/Exercise Equipment (Average)" },
      { id: "entertainment.X9024_A", label: "2024 Audio (Average)" },
      { id: "entertainment.X9065_A", label: "2024 Reading (Average)" },
      { id: "HousingHousehold.X4063_A", label: "2024 Major Appliances (Average)" },
      { id: "clothing.X5001_A", label: "2024 Apparel & Services (Average)" },
      { id: "clothing.X5002_A", label: "2024 Men`s Apparel (Average)" },
      { id: "clothing.X5016_A", label: "2024 Women`s Apparel (Average)" },
      { id: "clothing.X5032_A", label: "2024 Children`s Apparel (Average)" },
      { id: "clothing.X5063_A", label: "2024 Footwear (Average)" },
      { id: "food.X1131_A", label: "2024 Meals at Restaurants/Other (Average)" },
      { id: "food.X1156_A", label: "2024 Food and Nonalcoholic Beverages at Fast Food (Average)" },
      { id: "food.X1157_A", label: "2024 Food and Nonalcoholic Beverages at Full Service Restaurants (Average)" },
      { id: "food.X1130_A", label: "2024 Food Away from Home (Average)" },
      { id: "food.X2007_A", label: "2024 Alcoholic Beverages Away from Home (Average)" },
      { id: "SpendingTotal.X15001_A", label: "2024 Retail Goods (Average)" },
      { id: "entertainment.X9036_A", label: "2024 Pet Food (Average)" },
      { id: "entertainment.X9037_A", label: "2024 Pets/Pet Supplies/Medicine for Pets (Average)" },
      { id: "entertainment.X9038_A", label: "2024 Pet Services (Average)" },
      { id: "entertainment.X9039_A", label: "2024 Vet Services (Average)" },
      { id: "transportation.X6011_A", label: "2024 Gasoline (Average)" },
      { id: "transportation.X6015_A", label: "2024 Vehicle Maintenance & Repairs (Average)" },
      { id: "HousingHousehold.X4043_I", label: "2024 Household Furnishings & Equipment (Index)" },
      { id: "entertainment.X9051_I", label: "2024 Sports/Rec/Exercise Equipment (Index)" },
      { id: "entertainment.X9024_I", label: "2024 Audio (Index)" },
      { id: "entertainment.X9065_I", label: "2024 Reading (Index)" },
      { id: "HousingHousehold.X4063_I", label: "2024 Major Appliances (Index)" },
      { id: "clothing.X5001_I", label: "2024 Apparel & Services (Index)" },
      { id: "clothing.X5002_I", label: "2024 Men`s Apparel (Index)" },
      { id: "clothing.X5016_I", label: "2024 Women`s Apparel (Index)" },
      { id: "clothing.X5032_I", label: "2024 Children`s Apparel (Index)" },
      { id: "clothing.X5063_I", label: "2024 Footwear (Index)" },
      { id: "food.X1131_I", label: "2024 Meals at Restaurants/Other (Index)" },
      { id: "food.X1156_I", label: "2024 Food and Nonalcoholic Beverages at Fast Food (Index)" },
      { id: "food.X1157_I", label: "2024 Food and Nonalcoholic Beverages at Full Service Restaurants (Index)" },
      { id: "food.X1130_I", label: "2024 Food Away from Home (Index)" },
      { id: "food.X2007_I", label: "2024 Alcoholic Beverages Away from Home (Index)" },
      { id: "SpendingTotal.X15001_I", label: "2024 Retail Goods (Index)" },
      { id: "entertainment.X9036_I", label: "2024 Pet Food (Index)" },
      { id: "entertainment.X9037_I", label: "2024 Pets/Pet Supplies/Medicine for Pets (Index)" },
      { id: "entertainment.X9038_I", label: "2024 Pet Services (Index)" },
      { id: "entertainment.X9039_I", label: "2024 Vet Services (Index)" },
      { id: "transportation.X6011_I", label: "2024 Gasoline (Index)" },
      { id: "transportation.X6015_I", label: "2024 Vehicle Maintenance & Repairs (Index)" },
      { id: "HousingHousehold.X4043FY_A", label: "2029 Household Furnishings & Equipment (Average)" },
      { id: "entertainment.X9051FY_A", label: "2029 Sports/Rec/Exercise Equipment (Average)" },
      { id: "entertainment.X9024FY_A", label: "2029 Audio (Average)" },
      { id: "entertainment.X9065FY_A", label: "2029 Reading (Average)" },
      { id: "HousingHousehold.X4063FY_A", label: "2029 Major Appliances (Average)" },
      { id: "clothing.X5001FY_A", label: "2029 Apparel & Services (Average)" },
      { id: "clothing.X5002FY_A", label: "2029 Men`s Apparel (Average)" },
      { id: "clothing.X5016FY_A", label: "2029 Women`s Apparel (Average)" },
      { id: "clothing.X5032FY_A", label: "2029 Children`s Apparel (Average)" },
      { id: "clothing.X5063FY_A", label: "2029 Footwear (Average)" },
      { id: "food.X1131FY_A", label: "2029 Meals at Restaurants/Other (Average)" },
      { id: "food.X1156FY_A", label: "2029 Food and Nonalcoholic Beverages at Fast Food (Average)" },
      { id: "food.X1157FY_A", label: "2029 Food and Nonalcoholic Beverages at Full Service Restaurants (Average)" },
      { id: "food.X1130FY_A", label: "2029 Food Away from Home (Average)" },
      { id: "food.X2007FY_A", label: "2029 Alcoholic Beverages Away from Home (Average)" },
      { id: "SpendingTotal.X15001FY_A", label: "2029 Retail Goods (Average)" },
      { id: "entertainment.X9036FY_A", label: "2029 Pet Food (Average)" },
      { id: "entertainment.X9037FY_A", label: "2029 Pets/Pet Supplies/Medicine for Pets (Average)" },
      { id: "entertainment.X9038FY_A", label: "2029 Pet Services (Average)" },
      { id: "entertainment.X9039FY_A", label: "2029 Vet Services (Average)" },
      { id: "transportation.X6011FY_A", label: "2029 Gasoline (Average)" },
      { id: "transportation.X6015FY_A", label: "2029 Vehicle Maintenance & Repairs (Average)" },
      { id: "HousingHousehold.X4043FY_I", label: "2029 Household Furnishings & Equipment (Index)" },
      { id: "entertainment.X9051FY_I", label: "2029 Sports/Rec/Exercise Equipment (Index)" },
      { id: "entertainment.X9024FY_I", label: "2029 Audio (Index)" },
      { id: "entertainment.X9065FY_I", label: "2029 Reading (Index)" },
      { id: "HousingHousehold.X4063FY_I", label: "2029 Major Appliances (Index)" },
      { id: "clothing.X5001FY_I", label: "2029 Apparel & Services (Index)" },
      { id: "clothing.X5002FY_I", label: "2029 Men`s Apparel (Index)" },
      { id: "clothing.X5016FY_I", label: "2029 Women`s Apparel (Index)" },
      { id: "clothing.X5032FY_I", label: "2029 Children`s Apparel (Index)" },
      { id: "clothing.X5063FY_I", label: "2029 Footwear (Index)" },
      { id: "food.X1131FY_I", label: "2029 Meals at Restaurants/Other (Index)" },
      { id: "food.X1156FY_I", label: "2029 Food and Nonalcoholic Beverages at Fast Food (Index)" },
      { id: "food.X1157FY_I", label: "2029 Food and Nonalcoholic Beverages at Full Service Restaurants (Index)" },
      { id: "food.X1130FY_I", label: "2029 Food Away from Home (Index)" },
      { id: "food.X2007FY_I", label: "2029 Alcoholic Beverages Away from Home (Index)" },
      { id: "SpendingTotal.X15001FY_I", label: "2029 Retail Goods (Index)" },
      { id: "entertainment.X9036FY_I", label: "2029 Pet Food (Index)" },
      { id: "entertainment.X9037FY_I", label: "2029 Pets/Pet Supplies/Medicine for Pets (Index)" },
      { id: "entertainment.X9038FY_I", label: "2029 Pet Services (Index)" },
      { id: "entertainment.X9039FY_I", label: "2029 Vet Services (Index)" },
      { id: "transportation.X6011FY_I", label: "2029 Gasoline (Index)" },
      { id: "transportation.X6015FY_I", label: "2029 Vehicle Maintenance & Repairs (Index)" },
      { id: "shopping.MP31198a_I", label: "2024 Ordered Home Furnishing Online Last 6 Mo (Index)" },
      { id: "shopping.MP31191a_I", label: "2024 Ordered Fitness Apparel/Equipment Online Last 6 Mo (Index)" },
      { id: "shopping.MP31212a_I", label: "2024 Ordered Stereo/Audio Equipment Online Last 6 Mo (Index)" },
      { id: "shopping.MP31176a_I", label: "2024 Ordered Book Online Last 6 Mo (Index)" },
      { id: "shopping.MP31201a_I", label: "2024 Ordered Household/Small Appliance Online Last 6 Mo (Index)" },
      { id: "shopping.MP31181a_I", label: "2024 Ordered Clothing/Apparel Online Last 6 Mo (Index)" },
      { id: "shopping.MP31172a_I", label: "2024 Ordered Automotive Product Online Last 6 Mo (Index)" },
      { id: "shopping.MP31206a_I", label: "2024 Ordered Pet Products/Supplies Online Last 6 Mo (Index)" },
      { id: "PsychographicsShopping.MP28805a_I", label: "2024 OK Buying Items Like Cars/Appliances Online: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28806a_I", label: "2024 OK Buying Items Like Cars/Appliances Online: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28807a_I", label: "2024 OK Buying Items Like Cars/Appliances Online: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28808a_I", label: "2024 OK Buying Items Like Cars/Appliances Online: 4-Agree Completely (Index)" },
      { id: "PsychographicsShopping.MP28809a_I", label: "2024 Only Shop at a Few Online Stores: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28810a_I", label: "2024 Only Shop at a Few Online Stores: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28811a_I", label: "2024 Only Shop at a Few Online Stores: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28812a_I", label: "2024 Only Shop at a Few Online Stores: 4-Agree Completely (Index)" },
      { id: "PsychographicsShopping.MP28833a_I", label: "2024 Research Online Before Buy Locally: 1-Disagree Completely (Index)" },
      { id: "PsychographicsShopping.MP28834a_I", label: "2024 Research Online Before Buy Locally: 2-Disagree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28835a_I", label: "2024 Research Online Before Buy Locally: 3-Agree Somewhat (Index)" },
      { id: "PsychographicsShopping.MP28836a_I", label: "2024 Research Online Before Buy Locally: 4-Agree Completely (Index)" }
    ]
  }
};

function createVariableLabels(categories) {
  const labels = {};
  Object.values(categories).forEach(category => {
    category.variables.forEach(variable => {
      // Store with full path ID
      labels[variable.id] = variable.label;
      
      // Also store with short key for lookup flexibility
      const shortKey = variable.id.split(".").pop();
      labels[shortKey] = variable.label;
    });
  });
  return labels;
}

// Initialize variable labels once
const variableLabels = createVariableLabels(analysisCategories);

// Helper functions
export const getAllVariables = () => {
  const tier1Vars = analysisCategories.tier1.variables.map(v => v.id);
  const tier2Vars = analysisCategories.tier2.variables.map(v => v.id);
  const retailVars = analysisCategories.retail.variables.map(v => v.id);
  const tapestryVars = analysisCategories.Tapestry.variables.map(v => v.id);
  return [...tier1Vars, ...tier2Vars, ...retailVars, ...tapestryVars];
};

export { variableLabels };

export class EnrichmentService {
  constructor() {
    this.clientId = ARCGIS_CLIENT_ID;
    this.clientSecret = ARCGIS_CLIENT_SECRET;
    this.token = null;
    this.tokenExpiration = null;
    this.variableLabels = variableLabels;
  }

  /**
   * Utility function to wait/delay execution
   * @param {number} ms - Milliseconds to wait
   */
  async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if an error is a rate limit error
   * @param {Error|Object} error - Error object to check
   */
  isRateLimitError(error) {
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorString = JSON.stringify(error).toLowerCase();

    // Check for common rate limit indicators
    return (
      errorMessage.includes('rate limit') ||
      errorMessage.includes('too many requests') ||
      errorMessage.includes('429') ||
      errorString.includes('rate limit') ||
      errorString.includes('too many requests') ||
      error?.status === 429 ||
      error?.code === 429
    );
  }

  formatNumberValue(value) {
    if (typeof value !== "number") return value;
    return value.toLocaleString("en-US", {
      maximumFractionDigits: 2,
    });
  }

  formatBigNumberAsText(value) {
    if (!value) return "";
    const numVal = parseFloat(value);
    if (Number.isNaN(numVal)) return value;
    return numVal.toLocaleString("fullwide", {
      useGrouping: false,
      maximumFractionDigits: 0
    });
  }

  getVariableLabel(variableId, vintageYear = '2025') {
    // First check for custom export names (user-defined overrides)
    const customExportNames = getCustomExportNames();
    const shortKey = variableId.split(".").pop();

    // Check custom names by full ID first, then by short key
    if (customExportNames[variableId]) {
      return customExportNames[variableId];
    }
    if (customExportNames[shortKey]) {
      return customExportNames[shortKey];
    }

    // Fall back to default label from our mapping
    let label = null;

    // Select the appropriate categories based on vintage year
    const categories = vintageYear === '2024' ? analysisCategories2024 : analysisCategories;

    if (this.variableLabels[variableId]) {
      label = this.variableLabels[variableId];
    } else {
      // If not found with full path, try with just the short key
      if (this.variableLabels[shortKey]) {
        label = this.variableLabels[shortKey];
      } else {
        // Direct lookup in the appropriate analysisCategories object based on vintage year
        // This ensures we catch all defined variables, even if the labels mapping wasn't properly initialized
        for (const categoryKey in categories) {
          const category = categories[categoryKey];
          for (const variable of category.variables) {
            if (variable.id === variableId || variable.id.endsWith(shortKey)) {
              label = variable.label;
              break;
            }
          }
          if (label) break;
        }
      }
    }

    // If we found a label, it should already have the correct year from the categories
    // But we still apply year replacement for any edge cases
    if (label) {
      const { currentYear, futureYear } = getLabelsForYear(vintageYear);

      // Replace year patterns in labels
      label = label.replace(/2025/g, currentYear);
      label = label.replace(/2030/g, futureYear);

      // Also handle 2024/2029 patterns if they exist
      label = label.replace(/2024/g, currentYear);
      label = label.replace(/2029/g, futureYear);

      return label;
    }

    // If all else fails, return the shortKey
    return shortKey;
  }

  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  async getToken() {
    if (this.token && this.tokenExpiration && Date.now() < this.tokenExpiration) {
      return this.token;
    }

    try {
      const tokenUrl = "https://www.arcgis.com/sharing/rest/oauth2/token";
      const params = new URLSearchParams({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        grant_type: "client_credentials",
        f: "json",
      });

      console.log("Requesting new token...");

      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token response not OK:", {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        throw new Error(`Token request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("Token response received");

      if (data.error) {
        throw new Error(`Token service error: ${JSON.stringify(data.error)}`);
      }

      if (!data.access_token) {
        throw new Error("No access_token received in response");
      }

      this.token = data.access_token;
      this.tokenExpiration = Date.now() + (data.expires_in - 60) * 1000;

      return this.token;
    } catch (error) {
      console.error("Token generation failed:", error);
      const maskedId = this.clientId ? `${this.clientId.slice(0, 6)}...` : "not set";
      throw new Error(`Token generation failed. Client ID: ${maskedId}. Error: ${error.message}`);
    }
  }

  async prepareGeometryForEnrichment(marketAreas) {
    await projection.load();

    const expandedAreas = [];
    let objectIdCounter = 0;  // Counter for ObjectID (unique identifier for API)

    for (let marketAreaIndex = 0; marketAreaIndex < marketAreas.length; marketAreaIndex++) {
      const area = marketAreas[marketAreaIndex];
      console.log("Processing market area for enrichment:", {
        name: area.name,
        type: area.type || area.ma_type,
        hasRadiusPoints: Boolean(area.radius_points?.length),
        hasDriveTimePoints: Boolean(area.drive_time_points?.length),
        hasLocations: Boolean(area.locations?.length),
      });

      // Check for both area.ma_type and area.type to handle different data structures
      if ((area.ma_type === "radius" || area.type === "radius")) {
        if (area.radius_points?.length > 0) {
          let largestRadiusInfo = { radius: 0, polygon: null };

          for (const point of area.radius_points) {
            console.log("Processing radius point:", JSON.stringify(point, null, 2));

            if (!point.center) {
              console.warn("Radius point has no center:", point);
              continue;
            }

            // Extract coordinates correctly based on available properties
            let centerLon, centerLat;

            // Handle different data structures
            if (point.center.longitude !== undefined && point.center.latitude !== undefined) {
              centerLon = point.center.longitude;
              centerLat = point.center.latitude;
            } else if (point.center.x !== undefined && point.center.y !== undefined) {
              centerLon = point.center.x;
              centerLat = point.center.y;
            } else {
              console.warn("Cannot determine center coordinates from point:", point.center);
              continue;
            }

            // Check if coordinates need to be transformed from Web Mercator to geographic
            const spatialRefWkid = point.center.spatialReference?.wkid ||
              point.center.spatialReference?.latestWkid;

            // CRITICAL FIX: Special handling for lat/long values incorrectly tagged as Web Mercator
            if (spatialRefWkid === 102100 || spatialRefWkid === 3857) {
              // If we have lat/long values with Web Mercator spatial ref
              if (point.center.longitude !== undefined && point.center.latitude !== undefined) {
                // Check if values appear to be latitude/longitude (reasonable range)
                if (Math.abs(centerLat) <= 90 && Math.abs(centerLon) <= 180) {
                  console.log("Detected lat/long values incorrectly tagged as Web Mercator - using directly");
                  // No conversion needed - keep the values as they are
                  console.log("Using coordinates directly:", { longitude: centerLon, latitude: centerLat });
                }
                else {
                  // Normal Web Mercator to geographic conversion for true Web Mercator coordinates
                  try {
                    const { default: Point } = await import("@arcgis/core/geometry/Point");
                    const { webMercatorToGeographic } = await import("@arcgis/core/geometry/support/webMercatorUtils");

                    const webMercatorPoint = new Point({
                      x: centerLon,
                      y: centerLat,
                      spatialReference: {
                        wkid: spatialRefWkid
                      }
                    });

                    const geographicPoint = webMercatorToGeographic(webMercatorPoint);

                    if (geographicPoint) {
                      centerLon = geographicPoint.longitude;
                      centerLat = geographicPoint.latitude;
                      console.log("Converted to geographic coordinates:", {
                        longitude: centerLon,
                        latitude: centerLat
                      });
                    }
                  } catch (error) {
                    console.error("Error converting to geographic coordinates:", error);
                  }
                }
              }
              // x/y coordinates need normal conversion
              else if (point.center.x !== undefined && point.center.y !== undefined) {
                try {
                  const { default: Point } = await import("@arcgis/core/geometry/Point");
                  const { webMercatorToGeographic } = await import("@arcgis/core/geometry/support/webMercatorUtils");

                  const webMercatorPoint = new Point({
                    x: centerLon,
                    y: centerLat,
                    spatialReference: {
                      wkid: spatialRefWkid
                    }
                  });

                  const geographicPoint = webMercatorToGeographic(webMercatorPoint);

                  if (geographicPoint) {
                    centerLon = geographicPoint.longitude;
                    centerLat = geographicPoint.latitude;
                    console.log("Converted Web Mercator x/y to geographic coordinates:", {
                      longitude: centerLon,
                      latitude: centerLat
                    });
                  }
                } catch (error) {
                  console.error("Error converting to geographic coordinates:", error);
                }
              }
            }

            // Skip if we have invalid coordinates
            if (!centerLon || !centerLat ||
              isNaN(centerLon) || isNaN(centerLat) ||
              (centerLon === 0 && centerLat === 0)) {
              console.warn("Invalid center coordinates:", centerLon, centerLat);
              continue;
            }

            console.log("Using center coordinates for enrichment:", { longitude: centerLon, latitude: centerLat });

            // Process each radius
            for (const radiusMiles of (point.radii || [])) {
              console.log(`Processing radius: ${radiusMiles} miles`);

              try {
                // Create a circle polygon directly in geographic coordinates
                const { default: Point } = await import("@arcgis/core/geometry/Point");
                const { geodesicBuffer } = await import("@arcgis/core/geometry/geometryEngine");

                // Create a geographic point
                const geoPoint = new Point({
                  longitude: centerLon,
                  latitude: centerLat,
                  spatialReference: { wkid: 4326 }
                });

                // Create a geodesic buffer (proper circle on Earth's surface)
                const radiusMeters = radiusMiles * 1609.34;
                const bufferPolygon = geodesicBuffer(geoPoint, radiusMeters, "meters");

                if (bufferPolygon && bufferPolygon.rings?.length > 0) {
                  // Log the first few points to verify they're valid
                  console.log(`Created buffer with ${bufferPolygon.rings[0].length} points. First 3:`,
                    JSON.stringify(bufferPolygon.rings[0].slice(0, 3)));

                  if (radiusMiles > largestRadiusInfo.radius) {
                    largestRadiusInfo = {
                      radius: radiusMiles,
                      polygon: bufferPolygon
                    };
                    console.log("Updated largest radius info:", radiusMiles);
                  }
                } else {
                  console.warn("Buffer creation failed for radius:", radiusMiles);
                }
              } catch (error) {
                console.error(`Error creating buffer for radius ${radiusMiles}:`, error);
              }
            }
          }

          if (largestRadiusInfo.polygon) {
            console.log("Using largest radius polygon:", largestRadiusInfo.radius);

            // Add the expanded area with the buffer polygon
            expandedAreas.push({
              geometry: {
                rings: largestRadiusInfo.polygon.rings,
                spatialReference: { wkid: 4326 }, // Ensure WGS84 for the API
              },
              attributes: {
                ObjectID: objectIdCounter,
                name: area.name || "Radius Area",
                originalAreaName: area.name || "Radius Area",
                radiusMiles: largestRadiusInfo.radius,
              },
              originalIndex: marketAreaIndex,  // Use original position in marketAreas array
            });
            objectIdCounter++;
          } else {
            console.warn(`No valid radius polygon created for market area: ${area.name}`);
          }
        } else {
          console.warn(`No radius_points found for radius MA: ${area.name}`);
        }
      } 
      // NEW CASE: Handle drivetime market areas 
      else if (area.ma_type === "drivetime" || area.type === "drivetime") {
        console.log("Processing drivetime market area:", area.name);
        
        // First check if we have a pre-computed polygon to use directly
        if (area.geometry && area.geometry.rings && area.geometry.rings.length > 0) {
          console.log("Using existing geometry for drivetime area");
          
          try {
            // Make sure the geometry is in the right projection
            const existingPolygon = new Polygon({
              rings: area.geometry.rings,
              spatialReference: area.geometry.spatialReference || { wkid: 3857 }
            });
            
            const projectedGeometry = projection.project(existingPolygon, {
              wkid: 4326
            });
            
            if (projectedGeometry) {
              expandedAreas.push({
                geometry: {
                  rings: projectedGeometry.rings,
                  spatialReference: { wkid: 4326 }
                },
                attributes: {
                  ObjectID: objectIdCounter,
                  name: area.name || "Drivetime Area",
                  originalAreaName: area.name || "Drivetime Area",
                  driveTimeMinutes: area.drive_time_minutes || 15
                },
                originalIndex: marketAreaIndex  // Use original position in marketAreas array
              });
              objectIdCounter++;
              continue; // Skip to next area since we've added this one
            }
          } catch (error) {
            console.error(`Error projecting existing drivetime geometry: ${error.message}`);
            // Continue to try other methods if this fails
          }
        }
        
        // Check if we have drive time points
        const driveTimePoints = Array.isArray(area.drive_time_points) ? 
          area.drive_time_points : 
          typeof area.drive_time_points === 'string' ? 
            JSON.parse(area.drive_time_points) : 
            [];
            
        if (driveTimePoints.length > 0) {
          console.log(`Found ${driveTimePoints.length} drive time points to process`);
          
          for (const point of driveTimePoints) {
            // Skip points without polygon data - we need the polygon
            if (!point.polygon) {
              console.warn("Skipping drive time point with no polygon:", point);
              continue;
            }
            
            try {
              // Extract the polygon from the point
              const driveTimePolygon = point.polygon;
              
              if (!driveTimePolygon.rings || driveTimePolygon.rings.length === 0) {
                console.warn("Drive time polygon has no rings:", driveTimePolygon);
                continue;
              }
              
              // Convert to Polygon object for projection
              const esriPolygon = new Polygon({
                rings: driveTimePolygon.rings,
                spatialReference: driveTimePolygon.spatialReference || { wkid: 3857 }
              });
              
              const projectedGeometry = projection.project(esriPolygon, {
                wkid: 4326
              });
              
              if (projectedGeometry) {
                // Get the drive time in minutes
                const driveTimeMinutes = point.timeRanges?.[0] ||
                  point.travelTimeMinutes ||
                  area.drive_time_minutes ||
                  15;

                expandedAreas.push({
                  geometry: {
                    rings: projectedGeometry.rings,
                    spatialReference: { wkid: 4326 }
                  },
                  attributes: {
                    ObjectID: objectIdCounter,
                    name: area.name || "Drivetime Area",
                    originalAreaName: area.name || "Drivetime Area",
                    driveTimeMinutes
                  },
                  originalIndex: marketAreaIndex  // Use original position in marketAreas array
                });
                objectIdCounter++;
                break; // Use only the first valid drive time polygon
              }
            } catch (error) {
              console.error(`Error processing drive time point:`, error);
            }
          }
        } else {
          console.warn(`No drive_time_points found for drivetime MA: ${area.name}`);
        }
      }
      else {
        // Enhanced processing for non-radius areas (states, counties, etc.)
        console.log(`Processing ${area.ma_type || area.type} area: ${area.name}`);
        
        const allRings = area.locations?.flatMap((loc) => loc.geometry?.rings || []) || [];

        if (!allRings.length) {
          console.warn(`No valid rings found for market area: ${area.name}`);
          continue;
        }

        try {
          // ENHANCED: Better handling for state-level and complex geometries
          const isStateLevelArea = (area.ma_type === "state" || area.type === "state");
          const hasMultipleLocations = area.locations && area.locations.length > 1;
          
          let finalGeometry = null;

          if (isStateLevelArea && hasMultipleLocations) {
            console.log(`Processing multi-state area with ${area.locations.length} states`);
            
            // For multi-state areas, union the geometries properly
            const { union, simplify } = await import("@arcgis/core/geometry/geometryEngine");
            const geometries = [];
            
            // Process each state individually first
            for (const location of area.locations) {
              if (location.geometry?.rings?.length > 0) {
                try {
                  const statePolygon = new Polygon({
                    rings: location.geometry.rings,
                    spatialReference: { wkid: 3857 }
                  });
                  
                  // Validate and simplify individual state geometry
                  const simplified = simplify(statePolygon);
                  if (simplified && this.isValidPolygon(simplified)) {
                    geometries.push(simplified);
                    console.log(`Added valid geometry for state: ${location.name}`);
                  } else {
                    console.warn(`Invalid or overly complex geometry for state: ${location.name}`);
                  }
                } catch (error) {
                  console.error(`Error processing state ${location.name}:`, error);
                }
              }
            }
            
            if (geometries.length === 0) {
              console.error(`No valid state geometries found for: ${area.name}`);
              continue;
            }
            
            // Union all state geometries
            try {
              let combinedGeometry = geometries[0];
              for (let i = 1; i < geometries.length; i++) {
                const unionResult = union([combinedGeometry, geometries[i]]);
                if (unionResult && this.isValidPolygon(unionResult)) {
                  combinedGeometry = unionResult;
                } else {
                  console.warn(`Union failed for geometry ${i}, skipping`);
                }
              }
              
              // Final simplification of the combined geometry
              const finalSimplified = simplify(combinedGeometry);
              if (finalSimplified && this.isValidPolygon(finalSimplified)) {
                finalGeometry = finalSimplified;
                console.log(`Successfully created union of ${geometries.length} state geometries`);
              } else {
                console.error(`Final union result is invalid for: ${area.name}`);
                continue;
              }
            } catch (error) {
              console.error(`Union operation failed for: ${area.name}`, error);
              continue;
            }
          } else {
            // Single location or non-state area - use existing logic with validation
            const combinedPolygon = new Polygon({
              rings: allRings,
              spatialReference: { wkid: 3857 },
            });
            
            // Validate the polygon before proceeding
            if (!this.isValidPolygon(combinedPolygon)) {
              console.error(`Invalid polygon geometry for: ${area.name}`);
              continue;
            }
            
            // Simplify complex geometries
            try {
              const { simplify } = await import("@arcgis/core/geometry/geometryEngine");
              const simplified = simplify(combinedPolygon);
              
              if (simplified && this.isValidPolygon(simplified)) {
                finalGeometry = simplified;
                console.log(`Simplified geometry for: ${area.name}`);
              } else {
                finalGeometry = combinedPolygon;
                console.log(`Using original geometry for: ${area.name}`);
              }
            } catch (error) {
              console.warn(`Simplification failed for ${area.name}, using original:`, error);
              finalGeometry = combinedPolygon;
            }
          }

          // Project to WGS84
          const projectedGeometry = projection.project(finalGeometry, {
            wkid: 4326,
          });

          if (projectedGeometry && this.isValidPolygon(projectedGeometry)) {
            // Additional validation for API limits
            const ringCount = projectedGeometry.rings.length;
            const totalPoints = projectedGeometry.rings.reduce((sum, ring) => sum + ring.length, 0);
            
            console.log(`Geometry stats for ${area.name}: ${ringCount} rings, ${totalPoints} total points`);
            
            // Check if geometry is too complex for the API
            if (totalPoints > 10000) {
              console.warn(`Geometry may be too complex for API (${totalPoints} points), attempting further simplification`);
              
              // Extract location IDs for cache key (works for zip, county, state, etc.)
              const areaType = area.ma_type || area.type;
              const locationIds = area.locations?.map(loc => loc.id || loc.name).filter(Boolean) || [];

              try {
                const { generalize } = await import("@arcgis/core/geometry/geometryEngine");
                const generalized = generalize(projectedGeometry, 0.001); // 0.001 degree tolerance

                if (generalized && this.isValidPolygon(generalized)) {
                  const newPointCount = generalized.rings.reduce((sum, ring) => sum + ring.length, 0);
                  console.log(`Generalized geometry to ${newPointCount} points`);

                  expandedAreas.push({
                    geometry: {
                      rings: generalized.rings,
                      spatialReference: { wkid: 4326 },
                    },
                    attributes: {
                      ObjectID: objectIdCounter,
                      name: area.name,
                      originalAreaName: area.name,
                      simplified: true,
                      originalPointCount: totalPoints,
                      finalPointCount: newPointCount
                    },
                    originalIndex: marketAreaIndex,  // Use original position in marketAreas array
                    // Location metadata for cache key (enables cross-project cache hits)
                    areaType: areaType,
                    locationIds: locationIds,
                  });
                } else {
                  throw new Error("Generalization produced invalid geometry");
                }
              } catch (error) {
                console.error(`Generalization failed for ${area.name}:`, error);
                // Still try with original geometry
                expandedAreas.push({
                  geometry: {
                    rings: projectedGeometry.rings,
                    spatialReference: { wkid: 4326 },
                  },
                  attributes: {
                    ObjectID: objectIdCounter,
                    name: area.name,
                    originalAreaName: area.name,
                    warning: "Complex geometry - may fail API validation"
                  },
                  originalIndex: marketAreaIndex,  // Use original position in marketAreas array
                  // Location metadata for cache key (enables cross-project cache hits)
                  areaType: areaType,
                  locationIds: locationIds,
                });
              }
            } else {
              // Geometry is within reasonable limits
              // Extract location IDs for cache key (works for zip, county, state, etc.)
              const areaType = area.ma_type || area.type;
              const locationIds = area.locations?.map(loc => loc.id || loc.name).filter(Boolean) || [];

              expandedAreas.push({
                geometry: {
                  rings: projectedGeometry.rings,
                  spatialReference: { wkid: 4326 },
                },
                attributes: {
                  ObjectID: objectIdCounter,
                  name: area.name,
                  originalAreaName: area.name,
                },
                originalIndex: marketAreaIndex,  // Use original position in marketAreas array
                // Location metadata for cache key (enables cross-project cache hits)
                areaType: areaType,
                locationIds: locationIds,
              });
            }

            objectIdCounter++;
          } else {
            console.error(`Projection returned null or invalid geometry for ${area.name}`);
          }
        } catch (error) {
          console.error(`Processing failed for ${area.name}:`, error);
        }
      }
    }

    // Final validation of expanded areas
    if (expandedAreas.length === 0) {
      throw new Error("No valid study areas could be generated from market areas");
    }

    // Enhanced validation of all polygons
    for (let i = 0; i < expandedAreas.length; i++) {
      const area = expandedAreas[i];
      if (!this.validateGeometryForAPI(area.geometry)) {
        console.error(`Invalid geometry for area ${i}:`, area);
        throw new Error(`Area at index ${i} (${area.attributes.name}) has invalid geometry for API submission`);
      }
    }

    console.log(`Successfully created ${expandedAreas.length} valid study areas for enrichment`);
    return expandedAreas;
  }

  isValidPolygon(polygon) {
    if (!polygon || !polygon.rings || !Array.isArray(polygon.rings)) {
      return false;
    }
    
    if (polygon.rings.length === 0) {
      return false;
    }
    
    // Check each ring
    for (const ring of polygon.rings) {
      if (!Array.isArray(ring) || ring.length < 4) {
        return false;
      }
      
      // Check for valid coordinates
      for (const point of ring) {
        if (!Array.isArray(point) || point.length < 2 || 
            typeof point[0] !== 'number' || typeof point[1] !== 'number' ||
            isNaN(point[0]) || isNaN(point[1])) {
          return false;
        }
      }
      
      // Check if ring is closed
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        return false;
      }
    }
    
    return true;
  }

  validateGeometryForAPI(geometry) {
    if (!geometry || !geometry.rings || !Array.isArray(geometry.rings) || geometry.rings.length === 0) {
      console.error("Geometry validation failed: missing or empty rings array");
      return false;
    }

    // Check if any ring has coordinates
    let hasValidRing = false;
    let totalPoints = 0;
    
    for (const ring of geometry.rings) {
      if (!Array.isArray(ring) || ring.length < 4) { // Need at least 4 points for a valid ring
        console.error("Geometry validation failed: ring has insufficient points", ring?.length);
        continue;
      }
      
      totalPoints += ring.length;
      
      // Check if ring has valid, non-zero coordinates
      const hasValidPoints = ring.every(point => {
        if (!Array.isArray(point) || point.length < 2) return false;
        const [x, y] = point;
        return typeof x === 'number' && typeof y === 'number' && 
              !isNaN(x) && !isNaN(y) && 
              Math.abs(x) > 0.000001 && Math.abs(y) > 0.000001; // Not exactly zero
      });
      
      if (hasValidPoints) {
        hasValidRing = true;
      }
    }

    if (!hasValidRing) {
      console.error("Geometry validation failed: no valid rings with non-zero coordinates");
      return false;
    }
    
    // Check for reasonable complexity limits
    if (totalPoints > 15000) {
      console.warn(`Geometry has high complexity: ${totalPoints} points - may cause API issues`);
    }
    
    // Check spatial reference
    if (!geometry.spatialReference || !geometry.spatialReference.wkid) {
      console.error("Geometry validation failed: missing spatial reference");
      return false;
    }
    
    // Ensure WGS84 for API
    if (geometry.spatialReference.wkid !== 4326) {
      console.error("Geometry validation failed: spatial reference must be WGS84 (4326)");
      return false;
    }

    return true;
  }


  createCirclePolygon(centerX, centerY, radiusMiles, fromWkid = 102100) {
    // Convert miles to meters
    const radiusMeters = radiusMiles * 1609.34;
    const numPoints = 32;
    const points = [];

    // Debug the input values
    console.log("Creating circle polygon with:", {
      centerX,
      centerY,
      radiusMiles,
      radiusMeters,
      fromWkid
    });

    // Check for invalid center coordinates
    if (!centerX || !centerY || isNaN(centerX) || isNaN(centerY) ||
      (centerX === 0 && centerY === 0)) {
      console.error("Invalid center coordinates for circle polygon:", centerX, centerY);
      return null;
    }

    // Generate points around the circle
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i * 2 * Math.PI) / numPoints;
      const dx = Math.cos(angle) * radiusMeters;
      const dy = Math.sin(angle) * radiusMeters;

      // Add the offset to the center coordinates
      const pointX = centerX + dx;
      const pointY = centerY + dy;

      // Log some points to verify
      if (i === 0 || i === numPoints / 4 || i === numPoints / 2) {
        console.log(`Circle point ${i}:`, [pointX, pointY]);
      }

      points.push([pointX, pointY]);
    }

    // Close the ring
    points.push(points[0]);

    // Validate the created ring
    if (this.isValidRing(points)) {
      return [points];
    } else {
      console.error("Failed to create valid circle polygon");
      return null;
    }
  }

  // Helper method to validate a ring of coordinates
  isValidRing(ring) {
    // Check if we have enough points for a valid ring
    if (!ring || ring.length < 4) {
      console.error("Ring has insufficient points:", ring?.length);
      return false;
    }

    // Check if all coordinates are identical (invalid area)
    const firstPoint = ring[0];
    const allSame = ring.every(point =>
      point[0] === firstPoint[0] && point[1] === firstPoint[1]
    );

    if (allSame) {
      console.error("All points in ring are identical");
      return false;
    }

    // Check if any coordinates are invalid
    const hasInvalidCoords = ring.some(point =>
      !point || point.length !== 2 ||
      isNaN(point[0]) || isNaN(point[1]) ||
      (point[0] === 0 && point[1] === 0)
    );

    if (hasInvalidCoords) {
      console.error("Ring contains invalid coordinates");
      return false;
    }

    return true;
  }

  async enrichChunk(studyAreas, selectedVariables = [], vintageYear = '2025', retryCount = 0) {
    try {
      const token = await this.getToken();
      const enrichmentUrl = "https://geoenrich.arcgis.com/arcgis/rest/services/World/geoenrichmentserver/Geoenrichment/enrich";

      console.log("Enriching chunk with variables:", selectedVariables);
      console.log("Using vintage year:", vintageYear);
      console.log("Study areas for enrichment:", studyAreas);

      if (!studyAreas?.[0]?.geometry) {
        throw new Error("Invalid study area geometry");
      }

      const studyAreasPayload = studyAreas.map((s) => ({
        geometry: {
          ...s.geometry,
          // Explicitly specify geometry type to ensure API parses each as separate polygon
          geometryType: "esriGeometryPolygon"
        },
        attributes: s.attributes,
      }));

      // DEBUG: Log detailed info about what's being sent to the API
      console.log(`[Enrichment API DEBUG] Preparing ${studyAreasPayload.length} study areas for API call`);
      if (studyAreasPayload.length > 0) {
        const firstArea = studyAreasPayload[0];
        console.log(`[Enrichment API DEBUG] First study area structure:`, {
          hasGeometry: !!firstArea.geometry,
          ringCount: firstArea.geometry?.rings?.length || 0,
          spatialRef: firstArea.geometry?.spatialReference,
          attributeKeys: Object.keys(firstArea.attributes || {}),
          firstRingPointCount: firstArea.geometry?.rings?.[0]?.length || 0,
          name: firstArea.attributes?.name || firstArea.attributes?.originalAreaName
        });
        if (studyAreasPayload.length > 1) {
          const secondArea = studyAreasPayload[1];
          // Verify second area has different geometry (distinct from first)
          const firstRingStr = JSON.stringify(firstArea.geometry?.rings?.[0]?.[0]);
          const secondRingStr = JSON.stringify(secondArea.geometry?.rings?.[0]?.[0]);
          const geometriesAreDifferent = firstRingStr !== secondRingStr;
          console.log(`[Enrichment API DEBUG] Second study area structure:`, {
            hasGeometry: !!secondArea.geometry,
            ringCount: secondArea.geometry?.rings?.length || 0,
            spatialRef: secondArea.geometry?.spatialReference,
            name: secondArea.attributes?.name || secondArea.attributes?.originalAreaName,
            geometriesAreDifferent: geometriesAreDifferent
          });
          if (!geometriesAreDifferent) {
            console.warn('[Enrichment API DEBUG] WARNING: First two study areas have SAME geometry! This may cause API issues.');
          }
        }
        // Log first 100 chars of the JSON payload structure
        const payloadPreview = JSON.stringify(studyAreasPayload).substring(0, 500);
        console.log('[Enrichment API DEBUG] Payload preview:', payloadPreview);
      }

      // --- Start of API Parameters ---
      const params = new URLSearchParams();
      params.append("f", "json");
      params.append("token", token);
      params.append("studyAreas", JSON.stringify(studyAreasPayload));
      params.append("analysisVariables", JSON.stringify(selectedVariables));
      params.append("returnGeometry", "false");
      // Explicitly specify input spatial reference to help API parse geometries correctly
      params.append("inSR", JSON.stringify({ wkid: 4326 }));

      // *** MODIFICATION: Explicitly set the data collection and hierarchy ***
      // Use the selected vintage year (2024 or 2025)
      // 2024 vintage contains 2022 current-year estimates and 2027 future-year projections
      // 2025 vintage contains 2023 current-year estimates and 2028 future-year projections
      const useDataObject = {
        sourceCountry: "US",
        hierarchy: `esri${vintageYear}` // Use the selected vintage year
      };
      params.append("useData", JSON.stringify(useDataObject));
      // --- End of API Parameters ---

      const response = await fetch(enrichmentUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Enrichment Error Response:", errorText);
        const error = new Error(`Enrichment request failed: ${response.status} - ${errorText}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();

      if (data.error) {
        console.error("Enrichment API Error:", data.error);
        throw new Error(`Enrichment API Error: ${data.error.message || JSON.stringify(data.error)}`);
      }

      if (!data.results || !Array.isArray(data.results)) {
        console.error("Invalid response format:", data);
        throw new Error("Invalid response format from enrichment service");
      }

      // DEBUG: Log how many results the Esri API returned vs how many were sent
      console.log(`[Enrichment API DEBUG] Sent ${studyAreas.length} study areas, received ${data.results.length} results`);
      if (data.results.length !== studyAreas.length) {
        console.warn(`[Enrichment API WARNING] Result count mismatch! Expected ${studyAreas.length}, got ${data.results.length}`);
        console.log('[Enrichment API DEBUG] Full response structure:', {
          hasResults: !!data.results,
          resultsLength: data.results?.length,
          firstResultKeys: data.results?.[0] ? Object.keys(data.results[0]) : [],
          firstResultValue: data.results?.[0]?.value ? {
            hasFeatureSet: !!data.results[0].value.FeatureSet,
            featureSetLength: data.results[0].value.FeatureSet?.length,
            firstFeatureSetFeatureCount: data.results[0].value.FeatureSet?.[0]?.features?.length
          } : 'no value'
        });
        // Check if multiple features are nested inside a single result
        const nestedFeatures = data.results?.[0]?.value?.FeatureSet?.[0]?.features;
        if (nestedFeatures && nestedFeatures.length > 1) {
          console.log(`[Enrichment API DEBUG] FOUND ${nestedFeatures.length} features nested in first result!`);
          console.log('[Enrichment API DEBUG] This explains the mismatch - API returns all data in nested features');
        }
      }

      return data;
    } catch (error) {
      console.error("Error in enrichChunk:", error);

      // Check if this is a rate limit error and we haven't exceeded max retries
      if (this.isRateLimitError(error) && retryCount < MAX_RETRIES) {
        // Calculate exponential backoff delay
        const retryDelay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
        console.warn(`Rate limit detected. Retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})...`);

        await this.delay(retryDelay);

        // Retry the request
        return this.enrichChunk(studyAreas, selectedVariables, vintageYear, retryCount + 1);
      }

      // If not a rate limit error or max retries exceeded, throw the error
      throw error;
    }
  }

  async enrichAreas(marketAreas, selectedVariables = [], includeUSAData = false, vintageYear = '2025') {
    const shouldIncludeUSA = typeof includeUSAData === "boolean" ? includeUSAData : false;

    console.log('enrichAreas called with:', {
      marketAreasCount: marketAreas?.length,
      variablesCount: selectedVariables?.length,
      includeUSAData: shouldIncludeUSA,
      vintageYear: vintageYear
    });

    if (!marketAreas?.length) {
      throw new Error("No market areas provided for enrichment");
    }

    const studyAreas = await this.prepareGeometryForEnrichment(marketAreas);
    if (!studyAreas?.length) {
      throw new Error("No valid study areas generated from market areas");
    }

    // =========================================
    // CACHE CHECK: Check if geometries are already cached
    // =========================================
    let cachedResults = [];
    let uncachedStudyAreas = [];
    let cacheHits = 0;
    let cacheMisses = 0;

    try {
      console.log('[EnrichmentCache] Checking cache for', studyAreas.length, 'study areas...');

      // Extract geometries for cache check
      const geometries = studyAreas.map(sa => sa.geometry);

      // Extract area metadata for location-based cache lookup
      const areaMetadata = studyAreas.map(sa => ({
        area_type: sa.areaType || null,
        location_ids: sa.locationIds || []
      }));

      // Check cache via backend API (with location metadata for cross-project cache hits)
      const cacheResponse = await enrichmentCacheAPI.check(geometries, selectedVariables, areaMetadata);

      if (cacheResponse?.results) {
        // Process cache results
        cacheResponse.results.forEach((cacheResult, idx) => {
          const studyArea = studyAreas[idx];

          if (cacheResult.status === 'hit') {
            // Full cache hit - use cached data
            cacheHits++;
            cachedResults.push({
              studyArea,
              attributes: cacheResult.cached_data,
              fromCache: true
            });
          } else {
            // Cache miss or partial - need to enrich
            cacheMisses++;
            uncachedStudyAreas.push({
              ...studyArea,
              cacheResult // Store cache result for potential partial merge later
            });
          }
        });

        console.log(`[EnrichmentCache] Cache check complete: ${cacheHits} hits, ${cacheMisses} misses`);
      } else {
        // Cache check failed or returned no results - fall back to enriching all
        console.log('[EnrichmentCache] Cache check returned no results, enriching all areas');
        uncachedStudyAreas = [...studyAreas];
        cacheMisses = studyAreas.length;
      }
    } catch (cacheError) {
      // Cache check failed - continue without cache
      console.warn('[EnrichmentCache] Cache check failed, continuing without cache:', cacheError.message);
      uncachedStudyAreas = [...studyAreas];
      cacheMisses = studyAreas.length;
    }

    // =========================================
    // ENRICH UNCACHED AREAS
    // =========================================
    const apiResults = [];

    if (uncachedStudyAreas.length > 0) {
      const chunks = this.chunkArray(uncachedStudyAreas, CHUNK_SIZE);
      const totalChunks = chunks.length;

      console.log(`[Enrichment] Processing ${uncachedStudyAreas.length} uncached areas in ${totalChunks} batches (PARALLEL)...`);

      // Process all batches in parallel for maximum speed
      const batchPromises = chunks.map(async (chunk, i) => {
        try {
          console.log(`Enriching batch ${i + 1}/${totalChunks} (${chunk.length} areas)...`);
          const response = await this.enrichChunk(chunk, selectedVariables, vintageYear);

          if (response?.results) {
            const batchResults = [];

            // Check if Esri returned all features nested in a single result
            const firstResultFeatures = response.results[0]?.value?.FeatureSet?.[0]?.features;
            const resultsMatchChunk = response.results.length === chunk.length;
            const hasNestedFeatures = firstResultFeatures && firstResultFeatures.length === chunk.length && !resultsMatchChunk;

            if (hasNestedFeatures) {
              console.log(`[Enrichment] Batch ${i + 1}: API returned ${firstResultFeatures.length} features nested in 1 result - extracting individually`);
              firstResultFeatures.forEach((feature, featureIdx) => {
                const attributes = feature.attributes || {};
                const fakeResult = {
                  value: {
                    FeatureSet: [{
                      features: [{ attributes }]
                    }]
                  }
                };
                batchResults.push({
                  studyArea: chunk[featureIdx],
                  result: fakeResult,
                  attributes: attributes,
                  fromCache: false
                });
              });
            } else {
              response.results.forEach((result, resultIdx) => {
                const featureSet = result.value?.FeatureSet?.[0];
                const attributes = featureSet?.features?.[0]?.attributes || {};
                batchResults.push({
                  studyArea: chunk[resultIdx],
                  result: result,
                  attributes: attributes,
                  fromCache: false
                });
              });
            }
            console.log(`Successfully enriched batch ${i + 1}/${totalChunks}`);
            return batchResults;
          }
          return [];
        } catch (error) {
          console.error(`Failed to enrich batch ${i + 1}/${totalChunks}:`, error);
          return []; // Return empty array on error, continue with other batches
        }
      });

      // Wait for all batches to complete in parallel
      const allBatchResults = await Promise.all(batchPromises);

      // Flatten results from all batches
      allBatchResults.forEach(batchResults => {
        apiResults.push(...batchResults);
      });

      console.log(`[Enrichment] All ${totalChunks} batches completed, total results: ${apiResults.length}`);

      // =========================================
      // STORE NEW RESULTS IN CACHE
      // =========================================
      if (apiResults.length > 0) {
        try {
          console.log('[EnrichmentCache] Storing', apiResults.length, 'new results in cache...');

          const resultsToStore = apiResults
            .filter(r => r.attributes && Object.keys(r.attributes).length > 0)
            .map(r => ({
              geometry: r.studyArea.geometry,
              variables: selectedVariables,
              data: r.attributes,
              // Include location metadata for cross-project cache hits
              area_type: r.studyArea.areaType || null,
              location_ids: r.studyArea.locationIds || []
            }));

          if (resultsToStore.length > 0) {
            await enrichmentCacheAPI.store(resultsToStore);
            console.log('[EnrichmentCache] Successfully stored results in cache');
          } else {
            console.warn('[EnrichmentCache] No valid results to store in cache');
          }
        } catch (storeError) {
          // Cache store failed - log but don't fail the enrichment
          console.warn('[EnrichmentCache] Failed to store results in cache:', storeError.message);
        }
      }
    }

    // =========================================
    // MERGE CACHED AND API RESULTS
    // =========================================
    // Create a map of originalIndex to results for proper ordering
    const resultsByIndex = new Map();

    // Add cached results - convert to same structure as Esri API response
    cachedResults.forEach(r => {
      // Create a structure that matches the Esri API response format
      // so exportToExcel can parse it correctly
      const fakeEsriResult = {
        value: {
          FeatureSet: [{
            features: [{
              attributes: {
                ...r.attributes,
                ObjectID: r.studyArea.attributes.ObjectID
              }
            }]
          }]
        }
      };
      resultsByIndex.set(r.studyArea.originalIndex, fakeEsriResult);
    });

    // Add API results - use the original Esri result structure
    apiResults.forEach(r => {
      resultsByIndex.set(r.studyArea.originalIndex, r.result);
    });

    // Build final results array in original order
    const results = [];
    studyAreas.forEach(sa => {
      const result = resultsByIndex.get(sa.originalIndex);
      if (result) {
        results.push(result);
      }
    });

    if (!results.length) {
      throw new Error("No enrichment results returned");
    }

    const idToIndexMap = {};
    studyAreas.forEach((sa) => {
      idToIndexMap[sa.attributes.ObjectID] = sa.originalIndex;
    });

    console.log(`Enrichment complete: ${results.length}/${studyAreas.length} areas (${cacheHits} from cache, ${apiResults.length} from API)`);

    return {
      results,
      studyAreas,
      idToIndexMap,
      includeUSAData: shouldIncludeUSA,
      metadata: {
        processedAt: new Date().toISOString(),
        marketAreasCount: marketAreas.length,
        studyAreasCount: studyAreas.length,
        variablesCount: selectedVariables.length,
        includeUSAData: shouldIncludeUSA,
        successfullyEnriched: results.length,
        cacheHits: cacheHits,
        cacheMisses: cacheMisses,
        apiCalls: apiResults.length
      }
    };
  }

  aggregateResults(groupedResults, area) {
    const aggregated = {};
    groupedResults.forEach((attributes) => {
      Object.keys(attributes).forEach((key) => {
        if (typeof attributes[key] === "number") {
          aggregated[key] = (aggregated[key] || 0) + attributes[key];
        } else {
          aggregated[key] = attributes[key];
        }
      });
    });
    return aggregated;
  }

  async handleExport(enrichmentData, marketAreas, selectedVariables, options = {}) {
    const { includeUSAData = false, vintageYear = '2025' } = options;
    const shouldIncludeUSA = typeof includeUSAData === "boolean"
      ? includeUSAData
      : Boolean(enrichmentData?.includeUSAData);

    if (!enrichmentData || !marketAreas || !selectedVariables) {
      throw new Error('Missing required export parameters');
    }

    try {
      // Get project ID directly from the market area or options
      const projectId = options.projectId ||  // Add this line to explicitly pass project ID
        marketAreas[0]?.project_id ||
        marketAreas[0]?.id;

      console.log('Project ID Details:', {
        projectId,
        firstMarketArea: marketAreas[0],
        marketAreasLength: marketAreas.length,
        options
      });

      if (!projectId) {
        console.error('Market area structure:', marketAreas[0]);
        throw new Error('Could not find project ID');
      }

      // Get user information from the API
      let userId;
      try {
        const userResponse = await api.get('/api/admin/users/me/');
        userId = userResponse.data.id;

        if (!userId || typeof userId !== 'number') {
          throw new Error('Invalid user ID received from API');
        }
      } catch (error) {
        console.error('Failed to get user ID:', error);
        throw new Error('Could not determine user ID. Please log in again.');
      }

      // Calculate cost: $1 per 1000 market areas * variables
      // Only charge for API calls (not cache hits)
      const { cacheHits = 0, apiCalls = 0 } = enrichmentData.metadata || {};
      const variablesCount = selectedVariables.length;

      // Actual cost = only areas that required API calls
      const apiRecords = apiCalls * variablesCount;
      const calculatedCost = apiRecords / 1000;
      const cost = apiRecords > 0 ? Math.max(calculatedCost, 0.01) : 0;

      // Saved cost = areas served from cache
      const cachedRecords = cacheHits * variablesCount;
      const calculatedSaved = cachedRecords / 1000;
      const costSaved = cachedRecords > 0 ? Math.max(calculatedSaved, 0.01) : 0;

      // Prepare the usage data
      const usageData = {
        user_id: userId,
        project_id: projectId,
        cost: parseFloat(cost.toFixed(2)),
        cost_saved: parseFloat(costSaved.toFixed(2))
      };

      console.log('Sending enrichment usage data:', usageData);
      console.log(`Cache savings: ${cacheHits} areas from cache, ${apiCalls} from API`);

      // Record the enrichment usage
      try {
        const usageResponse = await api.post('/api/enrichment/record_usage/', usageData);
        console.log('Enrichment usage recorded:', usageResponse.data);
      } catch (error) {
        const errorDetails = error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message;
        console.error('Failed to record enrichment usage:', {
          error: error.response?.data,
          details: errorDetails,
          status: error.response?.status,
          data: usageData
        });
        throw new Error(`Failed to record enrichment usage: ${errorDetails}`);
      }

      const exportResult = await this.exportToExcel(
        enrichmentData,
        marketAreas,
        selectedVariables,
        shouldIncludeUSA,
        vintageYear
      );

      return exportResult;
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error(`Export failed: ${error.message}`);
    }
  }

  // Helper method to handle the export once we have a project ID
  async handleExportWithProjectId(projectId, userId, marketAreas, selectedVariables, enrichmentData, includeUSAData, vintageYear = '2025') {
    const shouldIncludeUSA = typeof includeUSAData === "boolean"
      ? includeUSAData
      : Boolean(enrichmentData?.includeUSAData);

    console.log('Processing export:', {
      includeUSAData: shouldIncludeUSA,
      marketAreasCount: marketAreas?.length,
      variablesCount: selectedVariables?.length,
      userId,
      projectId
    });

    // Record the enrichment usage
    const usageResponse = await api.post('/api/enrichment/record_usage/', {
      user_id: userId,
      project_id: projectId,
      market_areas_count: marketAreas.length,
      variables_count: selectedVariables.length
    });

    console.log('Enrichment usage recorded:', usageResponse.data);

    const exportResult = await this.exportToExcel(
      enrichmentData,
      marketAreas,
      selectedVariables,
      shouldIncludeUSA,
      vintageYear
    );

    return exportResult;
  }

  addUSADataToRows(rows, selectedVariables, vintageYear = '2025') {
    // Add USA column headers
    rows[0].push("United States of America");
    rows[1].push("USA");
    rows[2].push("");
    rows[3].push("");
    rows[4].push("");

    // Process each selected variable and add its USA data
    // Start at row 5 (after the header rows)
    for (let i = 5; i < rows.length; i++) {
      const variableId = selectedVariables[i - 5];
      let usaValue = "";

      // Get the label for this variable using the correct vintage year
      const label = this.getVariableLabel(variableId, vintageYear);

      // Look up the USA value by label in the combined data map
      if (usaDataMap.has(label)) {
        usaValue = usaDataMap.get(label) || "";
      } else {
        // If not found, log a warning
        console.warn(`USA data not found for label: ${label} (ID: ${variableId})`);
      }

      // Add the USA value to the current row
      rows[i].push(usaValue);
    }
  }

  exportToExcel(enrichmentData, marketAreas, selectedVariables = [], includeUSAData = false, vintageYear = '2025') {
    const shouldIncludeUSAData = typeof includeUSAData === "boolean"
      ? includeUSAData
      : Boolean(enrichmentData?.includeUSAData);

    console.log("Starting exportToExcel with params:", {
      marketAreasCount: marketAreas?.length,
      selectedVariablesCount: selectedVariables?.length,
      includeUSAData: shouldIncludeUSAData,
      vintageYear: vintageYear
    });

    const { results, studyAreas, idToIndexMap } = enrichmentData;

    console.log("exportToExcel - idToIndexMap:", idToIndexMap);
    console.log("exportToExcel - results count:", results?.length);
    console.log("exportToExcel - studyAreas:", studyAreas?.map(sa => ({
      ObjectID: sa.attributes.ObjectID,
      originalIndex: sa.originalIndex,
      name: sa.attributes.name
    })));

    // Prepare enrichment lookup
    // The ArcGIS API returns all features inside a single result object's FeatureSet
    const enrichmentLookup = {};
    results.forEach((res, resIndex) => {
      console.log(`Processing result ${resIndex}:`, res);
      const featureSet = res.value?.FeatureSet?.[0];
      if (!featureSet?.features) {
        console.warn(`Result ${resIndex} has no valid featureSet/features`);
        return;
      }

      // Process ALL features in the FeatureSet, not just the first one
      console.log(`Result ${resIndex} has ${featureSet.features.length} features`);
      featureSet.features.forEach((feature, featureIndex) => {
        const attrs = feature.attributes;
        if (!attrs) {
          console.warn(`Feature ${featureIndex} in result ${resIndex} has no attributes`);
          return;
        }
        const objId = attrs.ObjectID;
        const originalIndex = idToIndexMap[objId];

        console.log(`Feature ${featureIndex}: ObjectID=${objId}, originalIndex=${originalIndex}`);

        if (originalIndex !== undefined) {
          enrichmentLookup[originalIndex] = attrs;
        } else {
          console.warn(`Feature ${featureIndex}: ObjectID ${objId} not found in idToIndexMap`);
        }
      });
    });

    console.log("enrichmentLookup keys:", Object.keys(enrichmentLookup));

    const rows = [];

    // Header rows
    rows.push(["Market Area Name", ...marketAreas.map(ma => ma.name || "")]);
    rows.push(["Short Name", ...marketAreas.map(ma => ma.short_name || "")]);
    rows.push([
      "Definition Type",
      ...marketAreas.map(ma => {
        const maType = ma.ma_type?.toLowerCase();
        return MA_TYPE_MAPPING[maType] || ma.ma_type?.toUpperCase() || "";
      })
    ]);

    // Areas Included row
    rows.push([
      "Areas Included",
      ...marketAreas.map(ma => {
        const maType = ma.ma_type?.toLowerCase();
        switch (maType) {
          case "zip":
            return ma.locations?.map(loc => this.formatBigNumberAsText(loc.name)).join(", ") || "";
          case "radius":
            if (ma.radius_points?.length > 0) {
              const allRadii = ma.radius_points.flatMap(p => p.radii || []);
              if (allRadii.length > 0) {
                const largestRadius = Math.max(...allRadii);
                return `${largestRadius} miles`;
              }
            }
            return "";
          case "block":
          case "blockgroup":
          case "tract":
            // Added error handling and proper this reference
            return ma.locations?.map(loc => {
              try {
                return this.formatBigNumberAsText(loc.name);
              } catch (e) {
                console.error(`Error formatting location name: ${loc.name}`, e);
                return loc.name || "";
              }
            }).join(", ") || "";
          default:
            return ma.locations?.map(loc => loc.name).join(", ") || "";
        }
      })
    ]);

    // Add a blank row after headers
    rows.push(Array(marketAreas.length + 1).fill(""));

    // Rest of the exportToExcel method remains the same...

    // Data rows with proper labels
    selectedVariables.forEach(variableId => {
      const shortKey = variableId.split(".").pop();
      const label = this.getVariableLabel(variableId, vintageYear);

      const values = marketAreas.map((_, idx) => {
        const attrs = enrichmentLookup[idx];
        if (!attrs) return "";
        const value = attrs[shortKey];
        // Return raw numeric value for Excel (not formatted as string)
        // This allows Excel to treat them as numbers for calculations and formatting
        return (value === null || value === undefined) ? "" : value;
      });

      rows.push([label, ...values]);
    });

    if (shouldIncludeUSAData) {
      this.addUSADataToRows(rows, selectedVariables, vintageYear);
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Set column widths
    const colWidths = {};
    rows[0].forEach((_, idx) => {
      colWidths[XLSX.utils.encode_col(idx)] = { width: 20 };  // Set default width
    });
    ws['!cols'] = Object.values(colWidths);

    // Apply number formatting to data cells (rows 5 onwards, columns 1 onwards)
    // This ensures numbers display with thousand separators and proper decimals
    const dataStartRow = 5; // Row 5 is where data starts (after headers)
    const dataStartCol = 1; // Column B is where data starts (after labels)

    for (let R = dataStartRow; R < rows.length; R++) {
      for (let C = dataStartCol; C < rows[R].length; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];

        if (cell && typeof cell.v === 'number') {
          // Check if the value is a whole number or has decimals
          // Use integer format for whole numbers, decimal format otherwise
          if (Number.isInteger(cell.v)) {
            cell.z = '#,##0';  // Integer format - no decimal point
          } else {
            cell.z = '#,##0.00';  // Decimal format - always show 2 decimal places
          }
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Market Area Analysis");

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }


  getStateFullName(stateAbbr) {
    if (!stateAbbr) return "";

    const stateMap = {
      AL: "Alabama",
      AK: "Alaska",
      AZ: "Arizona",
      AR: "Arkansas",
      CA: "California",
      CO: "Colorado",
      CT: "Connecticut",
      DE: "Delaware",
      FL: "Florida",
      GA: "Georgia",
      HI: "Hawaii",
      ID: "Idaho",
      IL: "Illinois",
      IN: "Indiana",
      IA: "Iowa",
      KS: "Kansas",
      KY: "Kentucky",
      LA: "Louisiana",
      ME: "Maine",
      MD: "Maryland",
      MA: "Massachusetts",
      MI: "Michigan",
      MN: "Minnesota",
      MS: "Mississippi",
      MO: "Missouri",
      MT: "Montana",
      NE: "Nebraska",
      NV: "Nevada",
      NH: "New Hampshire",
      NJ: "New Jersey",
      NM: "New Mexico",
      NY: "New York",
      NC: "North Carolina",
      ND: "North Dakota",
      OH: "Ohio",
      OK: "Oklahoma",
      OR: "Oregon",
      PA: "Pennsylvania",
      RI: "Rhode Island",
      SC: "South Carolina",
      SD: "South Dakota",
      TN: "Tennessee",
      TX: "Texas",
      UT: "Utah",
      VT: "Vermont",
      VA: "Virginia",
      WA: "Washington",
      WV: "West Virginia",
      WI: "Wisconsin",
      WY: "Wyoming",
      DC: "District of Columbia",
    };

    return stateMap[stateAbbr.trim().toUpperCase()] || stateAbbr;
  }
}

// Export an instance for direct usage
export const enrichmentService = new EnrichmentService();
